
import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import Image from '@/components/AppImage/AppImage';
import dayjs, { type Dayjs } from 'dayjs';
import type { Hotel, HotelRate, HotelRoom, HotelDetailsResponse, HotelSearchPricing } from '@/services/nuiteeBookingService';
import { nuiteeBookingService } from '@/services/nuiteeBookingService';
import { log, error as logError } from '@/utils/logger';
import { WebAnalytics } from '@/lib/analytics';
import {
  EVENT_BOOKING_HOTEL_VIEWED,
  EVENT_BOOKING_HOTEL_TAB_CLICKED,
  EVENT_BOOKING_GALLERY_OPENED,
  EVENT_BOOKING_FACILITIES_MODAL_OPENED,
  EVENT_BOOKING_REVIEWS_MODAL_OPENED,
  EVENT_BOOKING_RATE_FILTER_APPLIED,
} from '@/lib/analyticsEvents';
import { parseLocalDate, formatDateToYYYYMMDD } from '@/utils/dateUtils';
import logoIcon from '@/assets/logo-orange.svg';
import HotelDetailSearchBar from './HotelDetailSearchBar';
import NuiteeTermsContent from './NuiteeTermsContent';
import NuiteePrivacyContent from './NuiteePrivacyContent';
import SandboxBanner from '../SandboxBanner/SandboxBanner';
import './HotelDetailView.css';

type TabId = 'overview' | 'facilities' | 'rooms' | 'reviews' | 'description';

// Facility category icon components
const FacilityIcon: React.FC<{ category: string }> = ({ category }) => {
  const iconProps = { width: 18, height: 18, viewBox: '0 0 18 18', fill: 'none' };
  
  switch (category) {
    case 'essentials':
      return (
        <svg {...iconProps} xmlns="http://www.w3.org/2000/svg">
          <rect x="2" y="3" width="14" height="12" rx="1" stroke="currentColor" strokeWidth="1.5"/>
          <path d="M5 3V1C5 0.447715 5.44772 0 6 0H12C12.5523 0 13 0.447715 13 1V3" stroke="currentColor" strokeWidth="1.5"/>
          <circle cx="9" cy="9" r="2" stroke="currentColor" strokeWidth="1.5"/>
          <path d="M9 11V13" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
        </svg>
      );
    case 'internet':
      return (
        <svg {...iconProps} xmlns="http://www.w3.org/2000/svg">
          <path d="M9 1C4.58172 1 1 4.58172 1 9C1 13.4183 4.58172 17 9 17C13.4183 17 17 13.4183 17 9C17 4.58172 13.4183 1 9 1Z" stroke="currentColor" strokeWidth="1.5"/>
          <path d="M9 1C6.79086 1 5 4.58172 5 9C5 13.4183 6.79086 17 9 17" stroke="currentColor" strokeWidth="1.5"/>
          <path d="M9 1C11.2091 1 13 4.58172 13 9C13 13.4183 11.2091 17 9 17" stroke="currentColor" strokeWidth="1.5"/>
          <path d="M1 9H17" stroke="currentColor" strokeWidth="1.5"/>
        </svg>
      );
    case 'parking':
      return (
        <svg {...iconProps} xmlns="http://www.w3.org/2000/svg">
          <rect x="2" y="3" width="14" height="12" rx="1" stroke="currentColor" strokeWidth="1.5"/>
          <circle cx="6" cy="11" r="1.5" stroke="currentColor" strokeWidth="1.5"/>
          <circle cx="12" cy="11" r="1.5" stroke="currentColor" strokeWidth="1.5"/>
          <path d="M5 3V1C5 0.447715 5.44772 0 6 0H12C12.5523 0 13 0.447715 13 1V3" stroke="currentColor" strokeWidth="1.5"/>
        </svg>
      );
    case 'dining':
      return (
        <svg {...iconProps} xmlns="http://www.w3.org/2000/svg">
          <path d="M5 2V16" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
          <path d="M9 2V16" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
          <path d="M5 9H9" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
          <path d="M13 2L13 12C13 13.1046 13.8954 14 15 14" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
        </svg>
      );
    case 'wellness':
      return (
        <svg {...iconProps} xmlns="http://www.w3.org/2000/svg">
          <circle cx="9" cy="9" r="6" stroke="currentColor" strokeWidth="1.5"/>
          <path d="M9 3V15M3 9H15" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
        </svg>
      );
    case 'fitness':
      return (
        <svg {...iconProps} xmlns="http://www.w3.org/2000/svg">
          <path d="M9 1V17" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
          <path d="M5 5L9 1L13 5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
          <path d="M5 13L9 17L13 13" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
      );
    case 'pool':
      return (
        <svg {...iconProps} xmlns="http://www.w3.org/2000/svg">
          <path d="M2 9C2 9 4 7 9 7C14 7 16 9 16 9" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
          <path d="M2 11C2 11 4 13 9 13C14 13 16 11 16 11" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
        </svg>
      );
    case 'business':
      return (
        <svg {...iconProps} xmlns="http://www.w3.org/2000/svg">
          <rect x="3" y="4" width="12" height="10" rx="1" stroke="currentColor" strokeWidth="1.5"/>
          <path d="M3 7H15" stroke="currentColor" strokeWidth="1.5"/>
          <path d="M7 4V2C7 1.44772 7.44772 1 8 1H10C10.5523 1 11 1.44772 11 2V4" stroke="currentColor" strokeWidth="1.5"/>
        </svg>
      );
    case 'accessibility':
      return (
        <svg {...iconProps} xmlns="http://www.w3.org/2000/svg">
          <circle cx="9" cy="6" r="3" stroke="currentColor" strokeWidth="1.5"/>
          <path d="M4 16C4 12 6 10 9 10C12 10 14 12 14 16" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
          <circle cx="6" cy="13" r="1.5" stroke="currentColor" strokeWidth="1.5"/>
          <circle cx="12" cy="13" r="1.5" stroke="currentColor" strokeWidth="1.5"/>
        </svg>
      );
    case 'family':
      return (
        <svg {...iconProps} xmlns="http://www.w3.org/2000/svg">
          <circle cx="6" cy="5" r="2.5" stroke="currentColor" strokeWidth="1.5"/>
          <circle cx="12" cy="5" r="2.5" stroke="currentColor" strokeWidth="1.5"/>
          <circle cx="9" cy="11" r="2" stroke="currentColor" strokeWidth="1.5"/>
          <path d="M3 16C3 13 4 11 6 11C7 11 7.5 11.5 8 12C8.5 11.5 9 11 10 11C12 11 13 13 13 16" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
        </svg>
      );
    case 'pets':
      return (
        <svg {...iconProps} xmlns="http://www.w3.org/2000/svg">
          <circle cx="6" cy="7" r="2" stroke="currentColor" strokeWidth="1.5"/>
          <circle cx="12" cy="7" r="2" stroke="currentColor" strokeWidth="1.5"/>
          <path d="M4 9C4 9 5 11 9 11C13 11 14 9 14 9" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
          <path d="M9 11V13" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
        </svg>
      );
    case 'transport':
      return (
        <svg {...iconProps} xmlns="http://www.w3.org/2000/svg">
          <rect x="3" y="6" width="12" height="6" rx="1" stroke="currentColor" strokeWidth="1.5"/>
          <path d="M5 6V4C5 3.44772 5.44772 3 6 3H12C12.5523 3 13 3.44772 13 4V6" stroke="currentColor" strokeWidth="1.5"/>
          <circle cx="6.5" cy="12" r="1.5" stroke="currentColor" strokeWidth="1.5"/>
          <circle cx="11.5" cy="12" r="1.5" stroke="currentColor" strokeWidth="1.5"/>
        </svg>
      );
    case 'room':
      return (
        <svg {...iconProps} xmlns="http://www.w3.org/2000/svg">
          <rect x="2" y="4" width="14" height="10" rx="1" stroke="currentColor" strokeWidth="1.5"/>
          <path d="M2 7H16" stroke="currentColor" strokeWidth="1.5"/>
          <rect x="4" y="9" width="3" height="3" rx="0.5" stroke="currentColor" strokeWidth="1.5"/>
        </svg>
      );
    case 'general':
      return (
        <svg {...iconProps} xmlns="http://www.w3.org/2000/svg">
          <rect x="3" y="4" width="12" height="10" rx="1" stroke="currentColor" strokeWidth="1.5"/>
          <circle cx="9" cy="9" r="2" stroke="currentColor" strokeWidth="1.5"/>
        </svg>
      );
    case 'services':
      return (
        <svg {...iconProps} xmlns="http://www.w3.org/2000/svg">
          <circle cx="9" cy="5" r="3" stroke="currentColor" strokeWidth="1.5"/>
          <path d="M4 16C4 12 6 10 9 10C12 10 14 12 14 16" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
          <path d="M9 10V13" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
        </svg>
      );
    case 'activities':
      return (
        <svg {...iconProps} xmlns="http://www.w3.org/2000/svg">
          <circle cx="9" cy="9" r="7" stroke="currentColor" strokeWidth="1.5"/>
          <path d="M9 5L11 9L15 9.5L11.5 12L12 16L9 13.5L6 16L6.5 12L3 9.5L7 9L9 5Z" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round"/>
        </svg>
      );
    case 'technology':
      return (
        <svg {...iconProps} xmlns="http://www.w3.org/2000/svg">
          <rect x="2" y="4" width="14" height="9" rx="1" stroke="currentColor" strokeWidth="1.5"/>
          <path d="M6 13H12" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
          <path d="M9 13V15" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
        </svg>
      );
    default:
      return (
        <svg {...iconProps} xmlns="http://www.w3.org/2000/svg">
          <circle cx="9" cy="9" r="6" stroke="currentColor" strokeWidth="1.5"/>
          <path d="M9 5V13M5 9H13" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
        </svg>
      );
  }
};

// Get icon component for individual facility (used on main page and modal)
const IndividualFacilityIcon: React.FC<{ facilityName: string }> = ({ facilityName }) => {
  const lower = facilityName.toLowerCase();
  const iconProps = { width: 16, height: 16, viewBox: '0 0 16 16', fill: 'none' };

  // Determine category based on facility name
  if (lower.includes('restaurant') || lower.includes('dining') || lower.includes('food')) {
    return <FacilityIcon category="dining" />;
  }
  if (lower.includes('room service') || lower.includes('service')) {
    return (
      <svg {...iconProps} xmlns="http://www.w3.org/2000/svg">
        <path d="M8 1L2 4V7C2 11 8 15 8 15C8 15 14 11 14 7V4L8 1Z" stroke="currentColor" strokeWidth="1.5"/>
      </svg>
    );
  }
  if (lower.includes('terrace') || lower.includes('balcony') || lower.includes('outdoor')) {
    return (
      <svg {...iconProps} xmlns="http://www.w3.org/2000/svg">
        <rect x="2" y="3" width="12" height="10" rx="1" stroke="currentColor" strokeWidth="1.5"/>
        <path d="M5 8H11M2 13H14" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
      </svg>
    );
  }
  if (lower.includes('non-smoking') || lower.includes('smoking')) {
    return (
      <svg {...iconProps} xmlns="http://www.w3.org/2000/svg">
        <circle cx="8" cy="8" r="6" stroke="currentColor" strokeWidth="1.5"/>
        <path d="M4 4L12 12" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
      </svg>
    );
  }
  if (lower.includes('disabled') || lower.includes('wheelchair') || lower.includes('accessibility')) {
    return <FacilityIcon category="accessibility" />;
  }
  if (lower.includes('family') || lower.includes('children') || lower.includes('kids')) {
    return <FacilityIcon category="family" />;
  }
  if (lower.includes('elevator') || lower.includes('lift')) {
    return (
      <svg {...iconProps} xmlns="http://www.w3.org/2000/svg">
        <rect x="4" y="1" width="8" height="14" rx="1" stroke="currentColor" strokeWidth="1.5"/>
        <path d="M6 5L8 3L10 5M6 11L8 13L10 11" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
      </svg>
    );
  }
  if (lower.includes('express check') || lower.includes('check-in') || lower.includes('check-out')) {
    return (
      <svg {...iconProps} xmlns="http://www.w3.org/2000/svg">
        <path d="M3 8L7 12L13 4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
      </svg>
    );
  }
  if (lower.includes('wifi') || lower.includes('internet')) {
    return <FacilityIcon category="internet" />;
  }
  if (lower.includes('parking') || lower.includes('garage')) {
    return <FacilityIcon category="parking" />;
  }
  if (lower.includes('luggage') || lower.includes('storage')) {
    return (
      <svg {...iconProps} xmlns="http://www.w3.org/2000/svg">
        <rect x="3" y="4" width="10" height="9" rx="1" stroke="currentColor" strokeWidth="1.5"/>
        <path d="M6 4V3C6 2.44772 6.44772 2 7 2H9C9.55228 2 10 2.44772 10 3V4" stroke="currentColor" strokeWidth="1.5"/>
      </svg>
    );
  }
  if (lower.includes('air conditioning') || lower.includes('heating')) {
    return (
      <svg {...iconProps} xmlns="http://www.w3.org/2000/svg">
        <circle cx="8" cy="8" r="5" stroke="currentColor" strokeWidth="1.5"/>
        <path d="M8 3V13M3 8H13M5 5L11 11M11 5L5 11" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
      </svg>
    );
  }
  if (lower.includes('safety deposit') || lower.includes('safe')) {
    return (
      <svg {...iconProps} xmlns="http://www.w3.org/2000/svg">
        <rect x="2" y="3" width="12" height="10" rx="1" stroke="currentColor" strokeWidth="1.5"/>
        <circle cx="8" cy="8" r="2" stroke="currentColor" strokeWidth="1.5"/>
      </svg>
    );
  }
  if (lower.includes('currency') || lower.includes('exchange')) {
    return (
      <svg {...iconProps} xmlns="http://www.w3.org/2000/svg">
        <circle cx="8" cy="8" r="6" stroke="currentColor" strokeWidth="1.5"/>
        <path d="M6 6C6 5.44772 6.44772 5 7 5H9C9.55228 5 10 5.44772 10 6V10C10 10.5523 9.55228 11 9 11H7C6.44772 11 6 10.5523 6 10" stroke="currentColor" strokeWidth="1.5"/>
      </svg>
    );
  }
  if (lower.includes('tour') || lower.includes('desk')) {
    return (
      <svg {...iconProps} xmlns="http://www.w3.org/2000/svg">
        <circle cx="8" cy="5" r="2" stroke="currentColor" strokeWidth="1.5"/>
        <path d="M3 15L4 9H12L13 15" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
      </svg>
    );
  }
  if (lower.includes('fax') || lower.includes('photocopying')) {
    return (
      <svg {...iconProps} xmlns="http://www.w3.org/2000/svg">
        <rect x="3" y="2" width="10" height="12" rx="1" stroke="currentColor" strokeWidth="1.5"/>
        <path d="M6 5H10M6 8H10M6 11H8" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
      </svg>
    );
  }
  if (lower.includes('concierge')) {
    return (
      <svg {...iconProps} xmlns="http://www.w3.org/2000/svg">
        <circle cx="8" cy="5" r="3" stroke="currentColor" strokeWidth="1.5"/>
        <path d="M3 15C3 11.5 5 10 8 10C11 10 13 11.5 13 15" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
      </svg>
    );
  }
  if (lower.includes('pets') || lower.includes('pet')) {
    return (
      <svg {...iconProps} xmlns="http://www.w3.org/2000/svg">
        <ellipse cx="8" cy="10" rx="3" ry="2.5" stroke="currentColor" strokeWidth="1.5"/>
        <circle cx="5" cy="5" r="1.5" fill="currentColor"/>
        <circle cx="8" cy="4" r="1.5" fill="currentColor"/>
        <circle cx="11" cy="5" r="1.5" fill="currentColor"/>
        <circle cx="5" cy="8" r="1.5" fill="currentColor"/>
        <circle cx="11" cy="8" r="1.5" fill="currentColor"/>
      </svg>
    );
  }
  if (lower.includes('car hire') || lower.includes('car rental') || lower.includes('rental car')) {
    return (
      <svg {...iconProps} xmlns="http://www.w3.org/2000/svg">
        <path d="M3 10L4 6H12L13 10M2 10H14V13H2V10Z" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round"/>
        <circle cx="5" cy="13" r="1.5" fill="currentColor"/>
        <circle cx="11" cy="13" r="1.5" fill="currentColor"/>
      </svg>
    );
  }
  if (lower.includes('coffee shop') || lower.includes('coffee')) {
    return (
      <svg {...iconProps} xmlns="http://www.w3.org/2000/svg">
        <path d="M3 5H11C11.5523 5 12 5.44772 12 6V10C12 11.1046 11.1046 12 10 12H4C2.89543 12 2 11.1046 2 10V6C2 5.44772 2.44772 5 3 5Z" stroke="currentColor" strokeWidth="1.5"/>
        <path d="M12 7H13C13.5523 7 14 7.44772 14 8C14 8.55228 13.5523 9 13 9H12" stroke="currentColor" strokeWidth="1.5"/>
        <path d="M4 12V14H10V12" stroke="currentColor" strokeWidth="1.5"/>
        <path d="M5 2C5 2 5.5 3 5.5 4M7 2C7 2 7.5 3 7.5 4M9 2C9 2 9.5 3 9.5 4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
      </svg>
    );
  }
  if (lower.includes('sanitized') || lower.includes('tableware') || lower.includes('silverware')) {
    return (
      <svg {...iconProps} xmlns="http://www.w3.org/2000/svg">
        <path d="M3 2V8C3 9 3.5 10 5 10V14" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
        <path d="M3 2V5M5 2V5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
        <path d="M11 2V6C11 7.5 10 8 9 8V14" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
        <circle cx="11" cy="3" r="0.5" fill="currentColor"/>
      </svg>
    );
  }
  if (lower.includes('shared lounge') || lower.includes('tv area') || lower.includes('lounge/tv')) {
    return (
      <svg {...iconProps} xmlns="http://www.w3.org/2000/svg">
        <rect x="2" y="4" width="12" height="8" rx="1" stroke="currentColor" strokeWidth="1.5"/>
        <path d="M5 12V14M11 12V14M3 14H13" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
        <circle cx="6" cy="8" r="1" fill="currentColor"/>
        <circle cx="10" cy="8" r="1" fill="currentColor"/>
      </svg>
    );
  }
  if (lower.includes('grocery') || lower.includes('deliveries')) {
    return (
      <svg {...iconProps} xmlns="http://www.w3.org/2000/svg">
        <path d="M2 2H3L4 4M4 4L5 11H13L14 4H4Z" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round"/>
        <circle cx="6" cy="13" r="1" fill="currentColor"/>
        <circle cx="12" cy="13" r="1" fill="currentColor"/>
      </svg>
    );
  }
  if (lower.includes('nightclub') || lower.includes('night club')) {
    return (
      <svg {...iconProps} xmlns="http://www.w3.org/2000/svg">
        <circle cx="8" cy="6" r="3" stroke="currentColor" strokeWidth="1.5"/>
        <path d="M5 9V14M8 9V14M11 9V14" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
        <path d="M4 14H12" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
      </svg>
    );
  }
  if (lower.includes('swimming') || lower.includes('pool')) {
    return (
      <svg {...iconProps} xmlns="http://www.w3.org/2000/svg">
        <rect x="2" y="5" width="12" height="7" rx="1" stroke="currentColor" strokeWidth="1.5"/>
        <path d="M3 8H5M7 8H9M11 8H13" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
        <path d="M4 10H6M8 10H10M12 10H14" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
      </svg>
    );
  }
  if (lower.includes('spa') || lower.includes('beauty') || lower.includes('massage') || lower.includes('sauna')) {
    return (
      <svg {...iconProps} xmlns="http://www.w3.org/2000/svg">
        <path d="M8 2C8 2 6 3 6 5C6 6.5 7 7.5 8 7.5C9 7.5 10 6.5 10 5C10 3 8 2 8 2Z" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round"/>
        <path d="M4 9C4 9 4 8 5 8C6 8 6 9 6 9M8 9C8 9 8 8 9 8C10 8 10 9 10 9M12 9C12 9 12 8 13 8C14 8 14 9 14 9" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
        <path d="M4 9V14M6 9V14M8 9V14M10 9V14M12 9V14M14 9V14" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
      </svg>
    );
  }
  if (lower.includes('education') || lower.includes('ecosystem') || lower.includes('culture') || lower.includes('learning')) {
    return (
      <svg {...iconProps} xmlns="http://www.w3.org/2000/svg">
        <path d="M2 4H14V13C14 13.5523 13.5523 14 13 14H3C2.44772 14 2 13.5523 2 13V4Z" stroke="currentColor" strokeWidth="1.5"/>
        <path d="M5 7H11M5 10H9" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
        <path d="M2 4V2C2 1.44772 2.44772 1 3 1H13C13.5523 1 14 1.44772 14 2V4" stroke="currentColor" strokeWidth="1.5"/>
      </svg>
    );
  }
  if (lower.includes('clubhouse') || lower.includes('club house') || lower.includes('social club')) {
    return (
      <svg {...iconProps} xmlns="http://www.w3.org/2000/svg">
        <path d="M8 1L2 5V13C2 13.5523 2.44772 14 3 14H13C13.5523 14 14 13.5523 14 13V5L8 1Z" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round"/>
        <rect x="6" y="9" width="4" height="5" stroke="currentColor" strokeWidth="1.5"/>
        <path d="M2 5H14" stroke="currentColor" strokeWidth="1.5"/>
      </svg>
    );
  }
  if (lower.includes('bbq') || lower.includes('barbecue') || lower.includes('grill') || lower.includes('barbeque')) {
    return (
      <svg {...iconProps} xmlns="http://www.w3.org/2000/svg">
        <ellipse cx="8" cy="6" rx="5" ry="3" stroke="currentColor" strokeWidth="1.5"/>
        <path d="M4 9L3 14M12 9L13 14" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
        <path d="M6 9V13M8 9V13M10 9V13" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
        <path d="M3 14H5M11 14H13" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
      </svg>
    );
  }
  // Default icon for unmapped facilities - more visible generic icon
  return (
    <svg {...iconProps} xmlns="http://www.w3.org/2000/svg">
      <circle cx="8" cy="8" r="6" stroke="currentColor" strokeWidth="1.5"/>
      <path d="M8 5V11M5 8H11" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
    </svg>
  );
};

// Facility category definitions matching whitelabel structure
const FACILITY_CATEGORIES: Record<string, { label: string; category: string; keywords: string[] }> = {
  general: {
    label: 'General',
    category: 'general',
    keywords: [
      'non-smoking', 'disabled', 'wheelchair', 'accessible', 'elevator', 'lift',
      'safety deposit', 'air conditioning', 'shared lounge', 'tv area',
      'coffee shop', '24-hour', 'security', 'alarm', 'smoke', 'cctv',
      'fire extinguisher', 'cleaning', 'guest rooms', 'distancing',
      'sanitizer', 'first aid', 'invoice', 'silverware', 'face mask',
      'atm', 'banking', 'covered parking', 'garage'
    ],
  },
  internet: {
    label: 'Internet',
    category: 'internet',
    keywords: ['wifi', 'wi-fi', 'internet'],
  },
  services: {
    label: 'Services',
    category: 'services',
    keywords: [
      'restaurant', 'room service', 'express check', 'luggage', 'concierge',
      'shopping', 'front desk', 'parking', 'fitness facilities'
    ],
  },
  activities: {
    label: 'Activities',
    category: 'activities',
    keywords: ['tour desk', 'television in common', 'concerts', 'live shows', 'charcoal grill'],
  },
  technology: {
    label: 'Technology',
    category: 'technology',
    keywords: ['terrace', 'family rooms', 'currency exchange', 'fax', 'photocopying', 'snack bar', 'deli'],
  },
};

// Group facilities by category
function categorizeFacilities(facilities: string[]): Map<string, string[]> {
  const categorized = new Map<string, string[]>();
  const uncategorized: string[] = [];
  
  facilities.forEach((facility) => {
    const lowerFacility = facility.toLowerCase();
    let foundCategory = false;
    
    for (const [categoryKey, category] of Object.entries(FACILITY_CATEGORIES)) {
      if (category.keywords.some((keyword) => lowerFacility.includes(keyword))) {
        const existing = categorized.get(categoryKey) || [];
        existing.push(facility);
        categorized.set(categoryKey, existing);
        foundCategory = true;
        break;
      }
    }
    
    if (!foundCategory) {
      uncategorized.push(facility);
    }
  });
  
  if (uncategorized.length > 0) {
    categorized.set('other', uncategorized);
  }
  
  return categorized;
}

// Filter and select facilities for main page display
function getPopularFacilitiesForMainPage(facilities: string[]): string[] {
  // Exclude unwanted categories
  const excludeKeywords = [
    'covid', 'coronavirus', 'distancing', 'disinfected', 'sanitizer',
    'face masks', 'safety protocols', 'barriers', 'cleaning standards',
    'physical distancing', 'delivered food', 'opt-out', 'shared stationery',
    'fire extinguisher', 'smoke alarm', 'cctv', 'security alarm',
    'first aid', 'cashless payment', 'invoice', 
    'charcoal grill', 'wine release', 'concerts', 'reception hall',
    'atm', 'banking', 'surcharge', 'additional charge'
  ];

  // Filter out unwanted facilities
  const filtered = facilities.filter(facility => {
    const lower = facility.toLowerCase();
    return !excludeKeywords.some(keyword => lower.includes(keyword));
  });

  // Remove duplicates based on semantic similarity
  const deduplicated: string[] = [];
  const seenTypes = new Set<string>();

  filtered.forEach(facility => {
    const lower = facility.toLowerCase();
    
    // Categorize by type to detect duplicates
    let facilityType = '';
    if (lower.includes('wifi') || lower.includes('internet')) {
      facilityType = 'internet';
    } else if (lower.includes('room service') || lower.includes('mobile app for room service')) {
      facilityType = 'room-service';
    } else if (lower.includes('parking') || lower.includes('garage')) {
      facilityType = 'parking';
    } else if (lower.includes('fitness')) {
      facilityType = 'fitness';
    } else {
      facilityType = facility; // Use facility name as unique type
    }

    // Skip if we've already seen this type (prefer first occurrence)
    if (!seenTypes.has(facilityType)) {
      seenTypes.add(facilityType);
      deduplicated.push(facility);
    }
  });

  // Always return exactly 14 facilities
  return deduplicated.slice(0, 14);
}

// Get 5 facilities for "Popular facilities" section in modal
function getPopularFacilitiesForModal(facilities: string[]): string[] {
  // Use same filtering as main page, just return first 5
  const mainPageFacilities = getPopularFacilitiesForMainPage(facilities);
  return mainPageFacilities.slice(0, 5);
}

interface HotelDetailViewProps {
  hotel: Hotel | null;
  checkIn: string;
  checkOut: string;
  adults: number;
  rooms: number;
  onRateSelected: (room: HotelRoom, rate: HotelRate) => void;
  onBack: () => void;
  onClose: () => void;
  onDatesChange?: (checkIn: string, checkOut: string, adults: number, rooms: number) => void;
  isSearchChanging?: boolean;
  searchChangeError?: string | null;
  isInitialLoading?: boolean;
  onWiderSearch?: () => void;
  isWiderSearchLoading?: boolean;
  pricingInfo?: HotelSearchPricing | null;
}

interface HotelDetails {
  id: string;
  name: string;
  address: string;
  city?: string;
  country?: string;
  rating?: number;
  starRating?: number;
  reviewCount?: number;
  hotelDescription?: string;
  hotelImportantInformation?: string;
  hotelImages?: Array<{ url: string; defaultImage?: boolean }>;
  hotelFacilities?: string[];
  checkinCheckoutTimes?: {
    checkin?: string;
    checkout?: string;
    checkinStart?: string;
    checkinEnd?: string;
  };
  policies?: Array<{
    policy_type?: string;
    type?: string;
    name?: string;
    text?: string;
    description?: string;
    child_allowed?: string;
    pets_allowed?: string;
    parking?: string;
  }>;
  location?: {
    latitude: number;
    longitude: number;
  };
  sentiment_analysis?: {
    pros?: string[];
    cons?: string[];
  };
  rooms?: Array<{
    id: number;
    roomName: string;
    photos?: Array<{ url: string }>;
    roomSizeSquare?: number;
    roomSizeUnit?: string;
    roomAmenities?: string[] | Array<{ id?: string; name?: string; [key: string]: unknown }>;
    description?: string;
    maxOccupancy?: number;
    maxAdults?: number;
    maxChildren?: number;
    bedTypes?: Array<
      | string
      | { quantity?: number; bedType?: string; bedSize?: string }
      | { id?: string; name?: string; count?: number; [key: string]: unknown }
    >;
  }>;
}

interface ReviewSummary {
  overallScore: number | null;
  reviewCount: number;
}

interface Review {
  id: string;
  reviewerName: string; // Always present (fallback to 'Guest' if missing)
  date: string | null; // May be null
  rating: number | null; // May be null, 0-10 scale
  text: string; // Always present (fallback to '' if missing)
  travelerType?: string | null;
  title?: string | null;
  positiveText?: string | null;
  negativeText?: string | null;
}

type ReviewSortOption = 'newest' | 'highest' | 'lowest';

// Returns "before Sat, Mar 28, 1:58 AM" from the first fee-incurring tier's cancelTime.
// Subtracts 1 minute from the fee-start time to get the last free moment, matching whitelabel behaviour.
function getFreeCancelDeadline(cancelPolicyInfos?: Array<{ cancelTime: string; amount: number; currency: string }>): string | null {
  const firstFeeTier = cancelPolicyInfos?.find(t => t.amount > 0);
  if (!firstFeeTier) return null;
  try {
    const d = new Date(firstFeeTier.cancelTime.replace(' ', 'T'));
    d.setMinutes(d.getMinutes() - 1);
    const date = d.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
    const time = d.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: true });
    return `before ${date}, ${time}`;
  } catch {
    return null;
  }
}

const HotelDetailView: React.FC<HotelDetailViewProps> = ({
  hotel,
  checkIn,
  checkOut,
  adults,
  rooms,
  onRateSelected,
  onBack,
  onClose,
  onDatesChange,
  isSearchChanging = false,
  searchChangeError = null,
  isInitialLoading = false,
  onWiderSearch,
  isWiderSearchLoading = false,
  pricingInfo,
}) => {
  const [activeTab, setActiveTab] = useState<TabId>('overview');
  const [hotelDetails, setHotelDetails] = useState<HotelDetails | null>(null);
  const [isLoadingDetails, setIsLoadingDetails] = useState(true);
  const [reviews, setReviews] = useState<Review[]>([]);
  const [reviewSummary, setReviewSummary] = useState<ReviewSummary | null>(null);
  const [isLoadingReviews, setIsLoadingReviews] = useState(false);
  const [reviewsPage, setReviewsPage] = useState(1);

  // Local state for search bar editing (editable values before "Change search" is clicked)
  const [editCheckIn, setEditCheckIn] = useState<Dayjs | null>(() => {
    const localDate = parseLocalDate(checkIn);
    return localDate ? dayjs(localDate) : null;
  });
  const [editCheckOut, setEditCheckOut] = useState<Dayjs | null>(() => {
    const localDate = parseLocalDate(checkOut);
    return localDate ? dayjs(localDate) : null;
  });
  const [editAdults, setEditAdults] = useState(adults);
  const [editRooms, setEditRooms] = useState(rooms);
  
  // Modal state for Configuring Rooms
  const [showRoomsConfigModal, setShowRoomsConfigModal] = useState(false);
  // Temporary values while modal is open
  const [tempAdults, setTempAdults] = useState(adults);
  const [tempRooms, setTempRooms] = useState(rooms);

  // Sync edit state when props change (after successful search change)
  useEffect(() => {
    const localCheckIn = parseLocalDate(checkIn);
    const localCheckOut = parseLocalDate(checkOut);
    setEditCheckIn(localCheckIn ? dayjs(localCheckIn) : null);
    setEditCheckOut(localCheckOut ? dayjs(localCheckOut) : null);
    setEditAdults(adults);
    setEditRooms(rooms);
  }, [checkIn, checkOut, adults, rooms]);

  // Smart handlers that preserve booking duration when check-in changes
  const handleCheckInChange = useCallback((newCheckIn: Dayjs | null) => {
    if (!newCheckIn) {
      setEditCheckIn(null);
      return;
    }

    // Calculate current number of nights
    const currentNights = editCheckIn && editCheckOut && editCheckOut.isAfter(editCheckIn)
      ? editCheckOut.diff(editCheckIn, 'day')
      : 2; // Default to 2 nights if no valid range exists

    // Set new check-in
    setEditCheckIn(newCheckIn);

    // Auto-adjust check-out to maintain the same number of nights
    const newCheckOut = newCheckIn.add(currentNights, 'day');
    setEditCheckOut(newCheckOut);
  }, [editCheckIn, editCheckOut]);

  const handleCheckOutChange = useCallback((newCheckOut: Dayjs | null) => {
    // Check-out changes don't need duration preservation
    setEditCheckOut(newCheckOut);
  }, []);

  // Check if backend is in sandbox mode
  useEffect(() => {
    const checkEnvironment = async () => {
      try {
        const response = await nuiteeBookingService.getEnvironment();
        setIsSandboxMode(response.data.isSandbox);
        log('[HotelDetailView] Environment check complete', {
          isSandbox: response.data.isSandbox,
          environment: response.data.environment,
        });
      } catch (err) {
        logError('[HotelDetailView] Failed to check environment', err);
        // Default to null (no banner shown) if check fails
        setIsSandboxMode(null);
      }
    };

    checkEnvironment();
  }, []);

  const [hasMoreReviews, setHasMoreReviews] = useState(false);
  const [reviewSort, setReviewSort] = useState<ReviewSortOption>('newest');
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [showAllImages, setShowAllImages] = useState(false);
  const [showFacilitiesModal, setShowFacilitiesModal] = useState(false);
  const [showReviewsModal, setShowReviewsModal] = useState(false);
  const [showRoomDetailsModal, setShowRoomDetailsModal] = useState(false);
  const [selectedRoomForDetails, setSelectedRoomForDetails] = useState<HotelRoom | null>(null);
  const [roomDetailsModalImageIndex, setRoomDetailsModalImageIndex] = useState(0);
  const [showTermsModal, setShowTermsModal] = useState(false);
  const [showPrivacyModal, setShowPrivacyModal] = useState(false);
  const [showContactModal, setShowContactModal] = useState(false);
  const [isSandboxMode, setIsSandboxMode] = useState<boolean | null>(null);

  const [filters, setFilters] = useState({
    breakfastIncluded: false,
    refundableOnly: false,
  });

  // Format date for display - parse in local timezone to avoid timezone shifts
  // Defined early so it can be used in useEffect
  const formatDate = useCallback((dateStr: string) => {
    // Parse date string (YYYY-MM-DD) in local timezone to avoid one-day-off issue
    const localDate = parseLocalDate(dateStr);
    if (!localDate) {
      log('[HotelDetailView] formatDate: Invalid date string', { dateStr });
      return dateStr; // Fallback to original string if parsing fails
    }
    
    const formatted = localDate.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
    });
    
    log('[HotelDetailView] formatDate', {
      input: dateStr,
      parsedLocalDate: localDate.toISOString(),
      formatted,
      localDateString: localDate.toLocaleDateString(),
    });
    
    return formatted;
  }, []);

  // Get rating label and color based on numerical rating (1-10 scale)
  // High (8-10): Green, Mid (6-7): Yellow, Low (1-5): Red
  const getRatingInfo = useCallback((score: number): { label: string; color: string } => {
    if (score >= 9) {
      return { label: 'Excellent', color: '#34c759' }; // Green
    } else if (score >= 8) {
      return { label: 'Very Good', color: '#34c759' }; // Green
    } else if (score >= 7) {
      return { label: 'Good', color: '#f5a623' }; // Yellow
    } else if (score >= 6) {
      return { label: 'Pleasant', color: '#f5a623' }; // Yellow
    } else if (score >= 5) {
      return { label: 'Fair', color: '#ff3b30' }; // Red
    } else {
      return { label: 'Poor', color: '#ff3b30' }; // Red
    }
  }, []);

  // Log dates when component receives them
  useEffect(() => {
    log('[HotelDetailView] Component received dates', {
      checkIn,
      checkOut,
      checkInType: typeof checkIn,
      checkOutType: typeof checkOut,
      checkInLength: checkIn?.length,
      checkOutLength: checkOut?.length,
      parsedCheckIn: checkIn ? parseLocalDate(checkIn)?.toISOString() : null,
      parsedCheckOut: checkOut ? parseLocalDate(checkOut)?.toISOString() : null,
      formattedCheckIn: checkIn ? formatDate(checkIn) : null,
      formattedCheckOut: checkOut ? formatDate(checkOut) : null,
    });
  }, [checkIn, checkOut, formatDate]);
  
  const sectionRefs = {
    overview: useRef<HTMLDivElement>(null),
    facilities: useRef<HTMLDivElement>(null),
    rooms: useRef<HTMLDivElement>(null),
    reviews: useRef<HTMLDivElement>(null),
    description: useRef<HTMLDivElement>(null),
  };
  
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const tabsRef = useRef<HTMLDivElement>(null);
  const viewedHotelIdRef = useRef<string | null>(null);

  // Fire hotel viewed event once per unique hotel
  useEffect(() => {
    if (hotel?.hotelId && hotel.hotelId !== viewedHotelIdRef.current) {
      viewedHotelIdRef.current = hotel.hotelId;
      WebAnalytics.track(EVENT_BOOKING_HOTEL_VIEWED, {
        hotel_id: hotel.hotelId,
        hotel_name: hotel.name,
        has_rates: (hotel.rates?.length ?? 0) > 0,
        rates_count: hotel.rates?.length ?? 0,
        check_in: checkIn,
        check_out: checkOut,
        adults,
        rooms,
      });
    }
  }, [hotel?.hotelId, hotel?.name, hotel?.rates, checkIn, checkOut, adults, rooms]);

  // Fetch hotel details on mount
  useEffect(() => {
    if (!hotel) {
      return;
    }

    const fetchDetails = async () => {
      setIsLoadingDetails(true);
      try {
        log('[HotelDetailView] Fetching hotel details', { hotelId: hotel.hotelId });
        const response = await nuiteeBookingService.getHotelDetails(hotel.hotelId, 'en') as HotelDetailsResponse;
        
        log('[HotelDetailView] Hotel details response status', {
          hotelId: hotel.hotelId,
          status: response.status,
          hasData: !!response.data,
          error: (response as HotelDetailsResponse & { error?: string }).error,
          fullResponse: JSON.stringify(response, null, 2).substring(0, 1000),
        });
        
        if (response.status === 'success' && response.data) {
          log('[HotelDetailView] Raw hotel details response', {
            hotelId: response.data.id,
            responseKeys: Object.keys(response.data),
            roomsKey: 'rooms' in response.data,
            roomsValue: response.data.rooms,
            roomsType: typeof response.data.rooms,
            roomsIsArray: Array.isArray(response.data.rooms),
            fullResponse: JSON.stringify(response.data, null, 2).substring(0, 1000),
          });
          
          setHotelDetails(response.data);
          log('[HotelDetailView] Hotel details loaded', {
            hotelId: response.data.id,
            hasImages: !!response.data.hotelImages?.length,
            hasFacilities: !!response.data.hotelFacilities?.length,
            hasDescription: !!response.data.hotelDescription,
            hasRooms: !!response.data.rooms,
            roomsCount: response.data.rooms?.length || 0,
            roomsWithPhotos: response.data.rooms?.filter((r) => r.photos && r.photos.length > 0).length || 0,
            roomsData: response.data.rooms?.slice(0, 2).map((r) => ({
              id: r.id,
              roomName: r.roomName,
              photosCount: r.photos?.length || 0,
              photosStructure: r.photos,
            })),
          });
        } else {
          log('[HotelDetailView] Hotel details request failed or returned no data', {
            hotelId: hotel.hotelId,
            status: response.status,
            error: (response as HotelDetailsResponse & { error?: string }).error,
            hasData: !!response.data,
          });
        }
      } catch (err) {
        logError('[HotelDetailView] Failed to fetch hotel details', err);
      } finally {
        setIsLoadingDetails(false);
      }
    };
    
    fetchDetails();
  // hotel object is recreated on each render; hotel?.hotelId is the stable identity key
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hotel?.hotelId]);

  // Auto-load reviews when hotel is set (and clear + reload when hotel changes)
  useEffect(() => {
    if (!hotel?.hotelId) return;
    setReviews([]);
    setReviewSummary(null);
    setReviewsPage(1);
    setHasMoreReviews(false);
    fetchReviews(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- run only when hotel changes
  }, [hotel?.hotelId]);

  // Fetch reviews when reviews section is viewed
  const fetchReviews = useCallback(async (page: number = 1, sort: ReviewSortOption = reviewSort) => {
    if (!hotel || isLoadingReviews) {
      log('[HotelDetailView] fetchReviews called but hotel is null or already loading, skipping');
      return;
    }
    
    log('[HotelDetailView] fetchReviews called', {
      hotelId: hotel.hotelId,
      page,
      sort,
      currentReviewsLength: reviews.length,
      currentReviewSummary: reviewSummary,
    });
    
    setIsLoadingReviews(true);
    try {
      log('[HotelDetailView] Calling nuiteeBookingService.getHotelReviews', {
        hotelId: hotel.hotelId,
        params: { page, limit: 10, sort, getSentiment: true },
      });
      
      const response = await nuiteeBookingService.getHotelReviews(hotel.hotelId, {
        page,
        limit: 10,
        sort,
        getSentiment: true, // Request sentiment analysis for summary
      });
      
      // Service returns { status: 'success', data: { reviews: [], summary: {}, pagination: {} } }
      log('[HotelDetailView] getHotelReviews response received - BEFORE processing', {
        status: response.status,
        hasData: !!response.data,
        dataKeys: response.data ? Object.keys(response.data) : [],
        hasReviews: !!response.data?.reviews,
        hasSummary: !!response.data?.summary,
        hasPagination: !!response.data?.pagination,
      });
      
      log('[HotelDetailView] getHotelReviews response received - AFTER extracting reviewsData', {
        status: response.status,
        hasData: !!response.data,
        reviewsCount: response.data?.reviews?.length || 0,
        hasSummary: !!response.data?.summary,
        summaryData: response.data?.summary,
        overallScore: response.data?.summary?.overallScore,
        reviewCount: response.data?.summary?.reviewCount,
        hasSentiment: !!response.data?.summary?.sentiment,
        hasPagination: !!response.data?.pagination,
        paginationData: response.data?.pagination,
        firstReview: response.data?.reviews?.[0],
      });
      
      // Check if we have valid data
      const reviewsData = response.data;
      
      if (response.status === 'success' && reviewsData) {
        
        if (page === 1) {
          log('[HotelDetailView] Setting reviews and summary (page 1)', {
            reviewsCount: reviewsData.reviews?.length || 0,
            summary: reviewsData.summary,
            overallScore: reviewsData.summary?.overallScore,
            hasSentiment: !!reviewsData.summary?.sentiment,
          });
          setReviews(reviewsData.reviews || []);
          setReviewSummary(reviewsData.summary || null);
        } else {
          log('[HotelDetailView] Appending reviews (page > 1)', {
            newReviewsCount: reviewsData.reviews?.length || 0,
            currentReviewsLength: reviews.length,
          });
          setReviews((prev) => [...prev, ...(reviewsData.reviews || [])]);
        }
        setHasMoreReviews(reviewsData.pagination?.hasMore || false);
        setReviewsPage(reviewsData.pagination?.page || page);
        log('[HotelDetailView] Reviews state updated', {
          reviewsCount: page === 1 ? (reviewsData.reviews?.length || 0) : reviews.length + (reviewsData.reviews?.length || 0),
          hasMore: reviewsData.pagination?.hasMore || false,
          page,
        });
      } else {
        log('[HotelDetailView] Response not successful or missing data', {
          status: response.status,
          hasData: !!response.data,
          dataKeys: response.data ? Object.keys(response.data) : [],
        });
      }
    } catch (err) {
      logError('[HotelDetailView] Failed to fetch reviews', {
        error: err,
        hotelId: hotel?.hotelId,
        page,
        sort,
        errorMessage: err instanceof Error ? err.message : String(err),
        errorStack: err instanceof Error ? err.stack : undefined,
      });
    } finally {
      setIsLoadingReviews(false);
      log('[HotelDetailView] fetchReviews completed, isLoadingReviews set to false');
    }
  }, [hotel, isLoadingReviews, reviewSort, reviews.length, reviewSummary]);

  // Handle sort change
  const handleReviewSortChange = useCallback((newSort: ReviewSortOption) => {
    if (newSort !== reviewSort) {
      setReviewSort(newSort);
      setReviews([]);
      setReviewsPage(1);
      fetchReviews(1, newSort);
    }
  }, [reviewSort, fetchReviews]);

  // Shared helper: get an element's top position relative to the scroll container
  // Uses getBoundingClientRect which works regardless of CSS positioning context
  const getSectionTop = useCallback((element: HTMLElement | null): number => {
    if (!element || !scrollContainerRef.current) return Infinity;
    const containerRect = scrollContainerRef.current.getBoundingClientRect();
    const elementRect = element.getBoundingClientRect();
    return elementRect.top - containerRect.top + scrollContainerRef.current.scrollTop;
  }, []);

  // Scroll to section when tab is clicked, accounting for sticky tabs height
  const handleTabClick = useCallback((tabId: TabId) => {
    setActiveTab(tabId);
    WebAnalytics.track(EVENT_BOOKING_HOTEL_TAB_CLICKED, {
      hotel_id: hotel?.hotelId || null,
      hotel_name: hotel?.name || null,
      tab: tabId,
    });
    
    // Load reviews if clicking on reviews tab and not loaded yet
    if (tabId === 'reviews' && reviews.length === 0 && !isLoadingReviews) {
      fetchReviews(1);
    }
    
    // For overview tab, scroll to the very top
    if (tabId === 'overview' && scrollContainerRef.current) {
      scrollContainerRef.current.scrollTo({
        top: 0,
        behavior: 'smooth'
      });
      return;
    }
    
    // Get the section to scroll to
    const sectionRef = sectionRefs[tabId as keyof typeof sectionRefs];
    const scrollContainer = scrollContainerRef.current;
    
    if (sectionRef?.current && scrollContainer) {
      const tabsHeight = tabsRef.current?.offsetHeight || 56;
      const sectionTop = getSectionTop(sectionRef.current);
      const scrollTarget = sectionTop - tabsHeight;
      
      scrollContainer.scrollTo({
        top: scrollTarget,
        behavior: 'smooth'
      });
    }
  // sectionRefs is an object literal defined in the component body — adding it would cause infinite loops
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchReviews, isLoadingReviews, reviews.length, getSectionTop, hotel?.hotelId, hotel?.name]);

  const handleOpenGallery = useCallback(() => {
    setShowAllImages(true);
    WebAnalytics.track(EVENT_BOOKING_GALLERY_OPENED, {
      hotel_id: hotel?.hotelId || null,
      hotel_name: hotel?.name || null,
    });
  }, [hotel?.hotelId, hotel?.name]);

  const handleOpenFacilitiesModal = useCallback(() => {
    setShowFacilitiesModal(true);
    WebAnalytics.track(EVENT_BOOKING_FACILITIES_MODAL_OPENED, {
      hotel_id: hotel?.hotelId || null,
      hotel_name: hotel?.name || null,
    });
  }, [hotel?.hotelId, hotel?.name]);

  const handleOpenReviewsModal = useCallback(() => {
    setShowReviewsModal(true);
    WebAnalytics.track(EVENT_BOOKING_REVIEWS_MODAL_OPENED, {
      hotel_id: hotel?.hotelId || null,
      hotel_name: hotel?.name || null,
    });
  }, [hotel?.hotelId, hotel?.name]);

  // Scroll listener to update active tab based on scroll position
  useEffect(() => {
    const scrollContainer = scrollContainerRef.current;
    if (!scrollContainer) return;

    const handleScroll = () => {
      const tabsHeight = tabsRef.current?.offsetHeight || 56;
      const scrollTop = scrollContainer.scrollTop;
      const triggerPoint = scrollTop + tabsHeight + 50;

      // Get all section positions using getBoundingClientRect
      const sections: Array<{ id: TabId; top: number }> = [];
      
      sections.push({ id: 'overview', top: getSectionTop(sectionRefs.overview.current) });
      sections.push({ id: 'facilities', top: getSectionTop(sectionRefs.facilities.current) });
      sections.push({ id: 'rooms', top: getSectionTop(sectionRefs.rooms.current) });
      sections.push({ id: 'reviews', top: getSectionTop(sectionRefs.reviews.current) });
      sections.push({ id: 'description', top: getSectionTop(sectionRefs.description.current) });

      // Filter out invalid sections and sort by position
      const validSections = sections.filter(s => s.top !== Infinity).sort((a, b) => a.top - b.top);

      // At the bottom of the page, always highlight the last section
      if (scrollTop + scrollContainer.clientHeight >= scrollContainer.scrollHeight - 5) {
        setActiveTab(validSections[validSections.length - 1]?.id || 'overview');
        return;
      }

      // Find which section we're currently viewing
      let currentSection: TabId = 'overview';
      
      for (let i = validSections.length - 1; i >= 0; i--) {
        if (triggerPoint >= validSections[i].top) {
          currentSection = validSections[i].id;
          break;
        }
      }

      setActiveTab(currentSection);
    };

    // Initial check after a brief delay to ensure DOM is ready
    const initialTimer = setTimeout(handleScroll, 100);

    scrollContainer.addEventListener('scroll', handleScroll, { passive: true });

    return () => {
      clearTimeout(initialTimer);
      scrollContainer.removeEventListener('scroll', handleScroll);
    };
  // sectionRefs.* are individual ref objects defined in the component body — adding them would cause infinite loops
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hotelDetails, getSectionTop]); // Re-run when hotel details change to ensure refs are ready

  // Get all images from hotel details or hotel object
  const allImages = useMemo(() => {
    const images: string[] = [];
    
    // Add images from hotel details
    if (hotelDetails?.hotelImages) {
      hotelDetails.hotelImages.forEach((img) => {
        if (img.url) images.push(img.url);
      });
    }
    
    // Add images from hotel object if no details images
    if (images.length === 0 && hotel?.images) {
      images.push(...hotel.images);
    }
    
    return images;
  }, [hotelDetails, hotel?.images]);

  // Create map of room photos from hotel details (keyed by mappedRoomId)
  const roomPhotosMap = useMemo(() => {
    const photosMap = new Map<number, string[]>();
    
    log('[HotelDetailView] Building room photos map', {
      hasHotelDetails: !!hotelDetails,
      hasRooms: !!hotelDetails?.rooms,
      roomsCount: hotelDetails?.rooms?.length || 0,
      roomsData: hotelDetails?.rooms?.map((r) => ({
        id: r.id,
        roomName: r.roomName,
        hasPhotos: !!r.photos,
        photosCount: r.photos?.length || 0,
        photosStructure: r.photos ? (Array.isArray(r.photos) ? 'array' : typeof r.photos) : 'none',
        firstPhoto: r.photos?.[0],
      })),
    });
    
    if (hotelDetails?.rooms) {
      hotelDetails.rooms.forEach((room) => {
        log('[HotelDetailView] Processing room for photos map', {
          roomId: room.id,
          roomName: room.roomName,
          hasPhotos: !!room.photos,
          photosType: typeof room.photos,
          isPhotosArray: Array.isArray(room.photos),
          photosLength: room.photos?.length || 0,
          photosData: room.photos,
        });
        
        if (room.id && room.photos && room.photos.length > 0) {
          const photoUrls = room.photos.map((p) => {
            const url = typeof p === 'string' ? p : p?.url;
            return url;
          }).filter((url) => url);
          
          if (photoUrls.length > 0) {
            photosMap.set(room.id, photoUrls);
            log('[HotelDetailView] Mapped room photos', {
              mappedRoomId: room.id,
              roomName: room.roomName,
              photoCount: photoUrls.length,
              photoUrls: photoUrls.slice(0, 2),
            });
          }
        }
      });
    }
    
    log('[HotelDetailView] Room photos map complete', {
      mapSize: photosMap.size,
      mappedIds: Array.from(photosMap.keys()),
    });
    
    return photosMap;
  }, [hotelDetails]);

  // Calculate nights for stay duration
  const nights = useMemo(() => {
    const checkInDate = parseLocalDate(checkIn);
    const checkOutDate = parseLocalDate(checkOut);
    if (!checkInDate || !checkOutDate) return 1;
    const nightsCount = Math.ceil((checkOutDate.getTime() - checkInDate.getTime()) / (1000 * 60 * 60 * 24));
    return nightsCount < 1 ? 1 : nightsCount;
  }, [checkIn, checkOut]);

  // Get minimum discounted price from available rates (for "from X/night" display)
  const minPrice = useMemo((): { price: number; currency: string } | null => {
    if (!hotel) return null;
    
    let minFound: { price: number; currency: string } | null = null;
    
    hotel.rooms?.forEach((room) => {
      room.rates?.forEach((rate) => {
        if (!minFound || rate.price < minFound.price) {
          minFound = { price: rate.price, currency: rate.currency };
        }
      });
    });
    
    return minFound;
  }, [hotel]);

  // Filter rooms based on filters and merge photos from hotel details
  const filteredRooms = useMemo(() => {
    if (!hotel || !hotel.rooms) return [];
    
    return hotel.rooms.map((room) => {
      // Get photos from hotel details by mappedRoomId
      const photosFromDetails = roomPhotosMap.get(room.mappedRoomId) || [];
      
      // Merge photos: prefer hotel details photos, fall back to room.roomImages
      const mergedPhotos = photosFromDetails.length > 0 
        ? photosFromDetails 
        : (room.roomImages || []);
      
      const filteredRates = room.rates.filter((rate) => {
        if (filters.breakfastIncluded && !rate.breakfastIncluded) return false;
        if (filters.refundableOnly && !rate.isRefundable) return false;
        return true;
      });
      
      return { 
        ...room, 
        rates: filteredRates,
        roomImages: mergedPhotos, // Override with merged photos
      };
    }).filter((room) => room.rates.length > 0);
  }, [hotel, filters, roomPhotosMap]);

  // Track current image index for each room's carousel
  const [roomImageIndices, setRoomImageIndices] = useState<Record<number, number>>({});
  
  const getRoomImageIndex = (roomId: number): number => {
    return roomImageIndices[roomId] || 0;
  };
  
  const setRoomImageIndex = (roomId: number, index: number) => {
    setRoomImageIndices((prev) => ({ ...prev, [roomId]: index }));
  };

  // Prepare room details modal data
  const roomDetailsModalData = useMemo(() => {
    if (!showRoomDetailsModal || !selectedRoomForDetails || !hotelDetails) {
      return null;
    }

    const roomDetailsData = hotelDetails.rooms?.find((r) => r.id === selectedRoomForDetails.mappedRoomId);
    const roomImages = selectedRoomForDetails.roomImages || [];
    
    // Extract room amenities - handle both string and object formats
    const rawAmenities = roomDetailsData?.roomAmenities || [];
    const roomAmenities = rawAmenities.map((a: unknown): string => {
      if (typeof a === 'string') return a;
      if (a && typeof a === 'object') {
        // Try common property names for amenity name
        const obj = a as Record<string, unknown>;
        if (typeof obj.name === 'string') return obj.name;
        if (typeof obj.amenity === 'string') return obj.amenity;
        if (typeof obj.title === 'string') return obj.title;
        if (typeof obj.description === 'string') return obj.description;
        // If it has an id, try to use that
        if (typeof obj.id === 'string') return obj.id;
        // Last resort - stringify
        return JSON.stringify(a);
      }
      return String(a);
    }).filter((a): a is string => typeof a === 'string' && a.length > 0);
    
    // Use hotel-level facilities for "Popular amenities" section to match whitelabel
    // Whitelabel shows hotel facilities (Restaurant, Room service, Terrace, etc.)
    // not room-specific amenities (Telephone, Air conditioning, etc.)
    const hotelFacilitiesList = hotelDetails.hotelFacilities || [];
    
    // Log room details modal data
    log('[HotelDetailView] Room details modal data', {
      mappedRoomId: selectedRoomForDetails.mappedRoomId,
      roomName: selectedRoomForDetails.roomName,
      foundRoomDetailsData: !!roomDetailsData,
      roomDetailsDataId: roomDetailsData?.id,
      roomAmenitiesExtracted: roomAmenities,
      roomAmenitiesCount: roomAmenities.length,
      hotelFacilitiesCount: hotelFacilitiesList.length,
      hotelFacilitiesFirst8: hotelFacilitiesList.slice(0, 8),
      hotelDetailsRoomsCount: hotelDetails.rooms?.length || 0,
      roomSizeSquare: roomDetailsData?.roomSizeSquare,
      roomSizeUnit: roomDetailsData?.roomSizeUnit,
      maxOccupancy: roomDetailsData?.maxOccupancy,
      description: roomDetailsData?.description,
      bedTypes: roomDetailsData?.bedTypes,
    });
    
    // Categorize room amenities for room-specific sections
    const categorizeAmenity = (amenity: string): string => {
      const lower = amenity.toLowerCase();
      if (lower.includes('internet') || lower.includes('wifi') || lower.includes('wi-fi') || lower.includes('telephone') || lower.includes('phone')) {
        return 'internet';
      }
      if (lower.includes('gym') || lower.includes('fitness') || lower.includes('conference') || lower.includes('business') || lower.includes('service')) {
        return 'services';
      }
      return 'general';
    };
    
    const generalAmenities = roomAmenities.filter(a => categorizeAmenity(a) === 'general');
    const internetAmenities = roomAmenities.filter(a => categorizeAmenity(a) === 'internet');
    const servicesAmenities = roomAmenities.filter(a => categorizeAmenity(a) === 'services');
    
    // Popular amenities: Use first 8 hotel facilities to match whitelabel display
    // Whitelabel shows hotel-level facilities here, not room amenities
    const popularAmenities = hotelFacilitiesList.slice(0, 8);
    
    // Get room size
    const roomSize = roomDetailsData?.roomSizeSquare && roomDetailsData?.roomSizeUnit
      ? `${roomDetailsData.roomSizeSquare} ${roomDetailsData.roomSizeUnit}`
      : null;
    
    // Get occupancy (prefer maxOccupancy, fallback to rates)
    const occupancy = roomDetailsData?.maxOccupancy || selectedRoomForDetails.rates[0]?.adultCount || 2;

    log('[HotelDetailView] Room details categorized', {
      generalAmenitiesCount: generalAmenities.length,
      internetAmenitiesCount: internetAmenities.length,
      servicesAmenitiesCount: servicesAmenities.length,
      popularAmenitiesCount: popularAmenities.length,
      roomSize,
      occupancy,
    });

    return {
      roomDetailsData,
      roomImages,
      roomAmenities,
      generalAmenities,
      internetAmenities,
      servicesAmenities,
      popularAmenities,
      roomSize,
      occupancy,
    };
  }, [showRoomDetailsModal, selectedRoomForDetails, hotelDetails]);

  // Currency code to prefix mapping (for whitelabel-style display)
  const getCurrencyPrefix = (currency: string): string => {
    const currencyPrefixMap: Record<string, string> = {
      USD: 'US',
      CAD: 'CA',
      EUR: 'EU',
      GBP: 'GB',
      AUD: 'AU',
      NZD: 'NZ',
      JPY: 'JP',
      CNY: 'CN',
      HKD: 'HK',
      SGD: 'SG',
      CHF: 'CH',
      SEK: 'SE',
      NOK: 'NO',
      DKK: 'DK',
      PLN: 'PL',
      CZK: 'CZ',
      HUF: 'HU',
      BRL: 'BR',
      MXN: 'MX',
      INR: 'IN',
      KRW: 'KR',
      THB: 'TH',
      MYR: 'MY',
      IDR: 'ID',
      PHP: 'PH',
      VND: 'VN',
      ZAR: 'ZA',
      TRY: 'TR',
      RUB: 'RU',
      ILS: 'IL',
      AED: 'AE',
      SAR: 'SA',
    };
    return currencyPrefixMap[currency.toUpperCase()] || currency.toUpperCase();
  };

  // Format price with currency prefix (e.g., "US$760" instead of "$760")
  const formatPrice = (price: number, currency: string) => {
    const prefix = getCurrencyPrefix(currency);
    
    // Format the price to get the currency symbol and formatted number
    const formatted = new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency,
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(price);
    
    // Extract currency symbol from formatToParts
    const parts = new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency,
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).formatToParts(price);
    
    const currencySymbol = parts.find(part => part.type === 'currency')?.value || '$';
    
    // Replace currency symbol with prefix + symbol (e.g., "$760" -> "US$760")
    return formatted.replace(currencySymbol, `${prefix}${currencySymbol}`);
  };


  // Render star rating
  const renderStars = (starRating: number) => {
    return Array(5).fill(0).map((_, i) => (
      <span key={i} className={`hotel-detail-star ${i < starRating ? 'filled' : ''}`}>
        ★
      </span>
    ));
  };

  // Handle rate selection
  const handleRateSelect = useCallback((room: HotelRoom, rate: HotelRate) => {
    log('[HotelDetailView] Rate selected', {
      hotelId: hotel?.hotelId,
      roomOfferId: room.offerId,
      rateId: rate.rateId,
      price: rate.price,
    });
    onRateSelected(room, rate);
  }, [hotel?.hotelId, onRateSelected]);

  // Handle "Change search" from either search bar (header or section)
  const handleChangeSearch = useCallback(() => {
    if (onDatesChange == null || editCheckIn == null || editCheckOut == null) return;
    if (!editCheckOut.isAfter(editCheckIn)) return;
    const checkInStr = formatDateToYYYYMMDD(editCheckIn.toDate());
    const checkOutStr = formatDateToYYYYMMDD(editCheckOut.toDate());
    log('[HotelDetailView] Change search', {
      checkIn: checkInStr,
      checkOut: checkOutStr,
      adults: editAdults,
      rooms: editRooms,
    });
    onDatesChange(checkInStr, checkOutStr, editAdults, editRooms);
  }, [onDatesChange, editCheckIn, editCheckOut, editAdults, editRooms]);


  // Compute display values (must be before any conditional returns to maintain hook order)
  const displayName = hotelDetails?.name || hotel?.name || 'Loading hotel...';
  const displayAddress = hotelDetails?.address || hotel?.address || '';
  const displayCity = hotelDetails?.city || hotel?.city;
  const displayCountry = hotelDetails?.country || hotel?.country;
  const starRating = hotelDetails?.starRating || 0;
  const rating = hotelDetails?.rating || hotel?.rating;
  const reviewCount = hotelDetails?.reviewCount || reviewSummary?.reviewCount || 0;
  
  const locationLabel = useMemo(() => {
    const parts = [];
    if (displayCity) parts.push(displayCity);
    if (displayCountry) parts.push(displayCountry.toUpperCase());
    return parts.join(', ') || 'Location';
  }, [displayCity, displayCountry]);

  // Render loading skeleton
  if (isLoadingDetails && !hotelDetails) {
    return (
      <div className="hotel-detail-view">
        <div className="hotel-detail-header">
          <button onClick={onBack} className="hotel-detail-back">← Back</button>
          <div className="hotel-detail-header-logo" aria-hidden>
            <Image src={logoIcon} alt="Globe Thrivers" width={100} height={28} style={{ objectFit: 'contain' }} />
          </div>
          <span className="hotel-detail-loading-text">Loading hotel details...</span>
        </div>
        <div className="hotel-detail-skeleton">
          <div className="skeleton-hero" />
          <div className="skeleton-content">
            <div className="skeleton-line wide" />
            <div className="skeleton-line medium" />
            <div className="skeleton-line narrow" />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="hotel-detail-view">
      {/* Loading Overlay for Initial Hotel Search */}
      {isInitialLoading && (
        <div style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(255, 255, 255, 0.95)',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000,
          gap: '16px',
        }}>
          <div className="booking-modal-loading-spinner" />
          <p style={{ fontSize: '16px', color: '#666' }}>Searching for available rooms...</p>
        </div>
      )}

      {/* Sticky Header */}
      <div className="hotel-detail-header">
        <button onClick={onBack} className="hotel-detail-back" aria-label="Back">
          <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M12 16L6 10L12 4" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </button>
        <div className="hotel-detail-header-logo" aria-hidden>
          <Image
            src={logoIcon}
            alt="Globe Thrivers"
            width={100}
            height={28}
            style={{ objectFit: 'contain' }}
          />
        </div>
        <HotelDetailSearchBar
          variant="header"
          editCheckIn={editCheckIn}
          editCheckOut={editCheckOut}
          editAdults={editAdults}
          editRooms={editRooms}
          onCheckInChange={handleCheckInChange}
          onCheckOutChange={handleCheckOutChange}
          onRoomsGuestsClick={() => {
            setTempAdults(editAdults);
            setTempRooms(editRooms);
            setShowRoomsConfigModal(true);
          }}
          onChangeSearch={handleChangeSearch}
          isSearchChanging={isSearchChanging}
          searchChangeError={searchChangeError}
          locationLabel={locationLabel}
          onWiderSearch={onWiderSearch}
          isWiderSearchLoading={isWiderSearchLoading}
        />
        {onWiderSearch && (
          <button
            onClick={isWiderSearchLoading ? undefined : onWiderSearch}
            disabled={isWiderSearchLoading}
            className="hotel-detail-mobile-whitelabel-search"
            aria-label="Search hotels in this area"
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <circle cx="11" cy="11" r="8" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"/>
              <path d="M21 21L16.65 16.65" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </button>
        )}
        <button
          onClick={onClose}
          className="hotel-detail-close"
          aria-label="Close"
        >
          ×
        </button>
        
        {/* Sandbox Mode Banner - shown below header buttons when in sandbox mode */}
        {isSandboxMode === true && (
          <div className="hotel-detail-header-banner">
            <SandboxBanner />
          </div>
        )}

        {/* Pricing Banner - GT+ or custom margin */}
        {pricingInfo?.margin_applied && pricingInfo?.message && (
          <div className={`hotel-detail-header-banner hotel-detail-pricing-banner hotel-detail-pricing-banner--${pricingInfo.pricing_tier}`}>
            <span className="hotel-detail-pricing-banner__icon">
              {pricingInfo.pricing_tier === 'gt_plus' ? '✦' : '◈'}
            </span>
            <span className="hotel-detail-pricing-banner__pill">
              {pricingInfo.pricing_tier === 'gt_plus' ? 'GT Plus' : 'Exclusive'}
            </span>
            <span className="hotel-detail-pricing-banner__message">
              {pricingInfo.message}
            </span>
          </div>
        )}
      </div>

      {/* Scrollable Content */}
      <div className="hotel-detail-content" ref={scrollContainerRef}>
        {/* Hero Section */}
        <div className="hotel-detail-hero">
          <div className="hotel-detail-hero-header">
            <div className="hotel-detail-name-section">
              <h2 className="hotel-detail-name">{displayName}</h2>
              {starRating > 0 && (
                <div className="hotel-detail-stars">{renderStars(starRating)}</div>
              )}
            </div>
            <div className="hotel-detail-address">
              <svg className="hotel-detail-address-icon" width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M7 7.58333C8.01252 7.58333 8.83333 6.76252 8.83333 5.75C8.83333 4.73748 8.01252 3.91667 7 3.91667C5.98748 3.91667 5.16667 4.73748 5.16667 5.75C5.16667 6.76252 5.98748 7.58333 7 7.58333Z" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M7 12.8333C9.33333 10.5 11.6667 8.36599 11.6667 5.75C11.6667 3.13401 9.57733 1.16667 7 1.16667C4.42267 1.16667 2.33333 3.13401 2.33333 5.75C2.33333 8.36599 4.66667 10.5 7 12.8333Z" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
              <span className="hotel-detail-address-text">
                {displayAddress}
                {displayCity && `, ${displayCity}`}
                {displayCountry && `, ${displayCountry.toUpperCase()}`}
              </span>
            </div>
          </div>

          {/* Image Gallery */}
          <div className="hotel-detail-gallery">
            {allImages.length > 0 ? (
              <>
                <div className="hotel-detail-hero-image" style={{ position: 'relative' }}>
                  <Image
                    src={allImages[currentImageIndex]}
                    alt={displayName}
                    fill
                    style={{ objectFit: 'cover' }}
                    onError={(e) => {
                      (e.target as HTMLImageElement).src = '/images/placeholder-hotel.png';
                    }}
                  />
                  {/* Mobile camera icon overlay */}
                  {allImages.length > 1 && (
                    <button
                      className="hotel-detail-mobile-camera-icon"
                      onClick={handleOpenGallery}
                      aria-label={`View all ${allImages.length} photos`}
                    >
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M23 19C23 19.5304 22.7893 20.0391 22.4142 20.4142C22.0391 20.7893 21.5304 21 21 21H3C2.46957 21 1.96086 20.7893 1.58579 20.4142C1.21071 20.0391 1 19.5304 1 19V8C1 7.46957 1.21071 6.96086 1.58579 6.58579C1.96086 6.21071 2.46957 6 3 6H7L9 3H15L17 6H21C21.5304 6 22.0391 6.21071 22.4142 6.58579C22.7893 6.96086 23 7.46957 23 8V19Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                        <path d="M12 17C14.2091 17 16 15.2091 16 13C16 10.7909 14.2091 9 12 9C9.79086 9 8 10.7909 8 13C8 15.2091 9.79086 17 12 17Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                      <span>{allImages.length}</span>
                    </button>
                  )}
                </div>
                {allImages.length > 1 && (
                  <div className="hotel-detail-thumbnails">
                    {allImages.slice(1, 4).map((img, idx) => (
                      <div
                        key={idx}
                        className={`hotel-detail-thumbnail ${currentImageIndex === idx + 1 ? 'active' : ''}`}
                        onClick={() => setCurrentImageIndex(idx + 1)}
                      >
                        <Image src={img} alt={`${displayName} ${idx + 2}`} fill style={{ objectFit: 'cover' }} />
                      </div>
                    ))}
                    {/* Last thumbnail with Show All overlay */}
                    {allImages.length > 4 && (
                      <div className="hotel-detail-thumbnail-wrapper" style={{ position: 'relative' }}>
                        <Image src={allImages[4]} alt={`${displayName} 5`} fill style={{ objectFit: 'cover' }} />
                        <button
                          className="hotel-detail-show-all"
                          onClick={handleOpenGallery}
                        >
                          <svg className="hotel-detail-show-all-icon" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <rect x="1" y="3" width="14" height="10" rx="2" stroke="currentColor" strokeWidth="1.5"/>
                            <circle cx="5" cy="7" r="1.5" stroke="currentColor" strokeWidth="1.5"/>
                            <path d="M1 11L4 8L6 10L10 6L15 11" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                          </svg>
                          Show all pictures
                        </button>
                      </div>
                    )}
                  </div>
                )}
              </>
            ) : (
              <div className="hotel-detail-no-image">
                <span>No images available</span>
              </div>
            )}
          </div>

        </div>

        {/* Tab Bar - with price and button on same line like whitelabel */}
        <div className="hotel-detail-tabs" ref={tabsRef}>
          <div className="hotel-detail-tabs-nav">
            {(['overview', 'facilities', 'rooms', 'reviews', 'description'] as TabId[]).map((tab) => (
              <button
                key={tab}
                className={`hotel-detail-tab ${activeTab === tab ? 'active' : ''}`}
                onClick={() => handleTabClick(tab)}
              >
                {tab.charAt(0).toUpperCase() + tab.slice(1)}
              </button>
            ))}
          </div>
          {/* Show pricing and "Select a room" button only in Overview and Facilities tabs */}
          {(activeTab === 'overview' || activeTab === 'facilities') && (
            <div className="hotel-detail-tabs-right">
              <div className="hotel-detail-tabs-price">
                {minPrice && (
                  <>
                    <span className="hotel-detail-tabs-price-label">from</span>
                    <span className="hotel-detail-tabs-price-value">
                      {formatPrice(Math.ceil(minPrice.price / nights), minPrice.currency)}
                    </span>
                    <span className="hotel-detail-tabs-price-night">/ night</span>
                  </>
                )}
              </div>
              <button
                onClick={() => handleTabClick('rooms')}
                className="hotel-detail-tabs-select-btn"
              >
                Select a room
              </button>
            </div>
          )}
        </div>

        {/* Overview Section - Review highlights */}
        <div ref={sectionRefs.overview} className="hotel-detail-section" id="overview">
          <div className="hotel-detail-review-highlights">
            <div className="hotel-detail-overview-header">
              <h3 className="hotel-detail-section-title">Review highlights</h3>
              {reviewCount > 0 && (
                <button
                  className="hotel-detail-read-all-reviews"
                  onClick={() => {
                    log('[HotelDetailView] Opening reviews modal', {
                      reviewCount,
                      reviewsLength: reviews.length,
                      isLoadingReviews,
                      hasReviewSummary: !!reviewSummary,
                      reviewSummaryData: reviewSummary,
                    });
                    handleOpenReviewsModal();
                    // Load reviews if not already loaded
                    if (reviews.length === 0 && !isLoadingReviews) {
                      log('[HotelDetailView] Reviews not loaded, fetching now');
                      fetchReviews(1);
                    } else {
                      log('[HotelDetailView] Reviews already loaded or loading', {
                        reviewsLength: reviews.length,
                        isLoadingReviews,
                      });
                    }
                  }}
                >
                  Read all reviews
                </button>
              )}
            </div>
            
            {/* Rating Summary */}
            {(rating || reviewCount > 0) && (
              <div className="hotel-detail-rating-summary">
                {rating && (
                  <div 
                    className="hotel-detail-rating-badge"
                    style={{ backgroundColor: getRatingInfo(rating).color }}
                  >
                    <span className="hotel-detail-rating-score">{rating.toFixed(1)}</span>
                  </div>
                )}
                <div className="hotel-detail-rating-info">
                  {rating && (
                    <span className="hotel-detail-rating-label">
                      {getRatingInfo(rating).label}
                    </span>
                  )}
                  {reviewCount > 0 && (
                    <span className="hotel-detail-review-count-text">
                      Based on {reviewCount.toLocaleString()} reviews
                    </span>
                  )}
                </div>
              </div>
            )}

            {/* What guests liked the most - styled as pills */}
            {hotelDetails?.sentiment_analysis?.pros && hotelDetails.sentiment_analysis.pros.length > 0 && (
              <div className="hotel-detail-guests-liked">
                <h4 className="hotel-detail-guests-liked-title">What did guests like the most</h4>
                <div className="hotel-detail-sentiment-pills">
                  {hotelDetails.sentiment_analysis.pros.slice(0, 3).map((pro, idx) => (
                    <span key={idx} className="hotel-detail-sentiment-pill">
                      {pro}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Facilities Section */}
        {hotelDetails?.hotelFacilities && hotelDetails.hotelFacilities.length > 0 && (
          <div ref={sectionRefs.facilities} className="hotel-detail-section" id="facilities">
            <div className="hotel-detail-popular-facilities">
              <div className="hotel-detail-overview-header">
                <h3 className="hotel-detail-section-title">Popular facilities</h3>
                {hotelDetails.hotelFacilities.length > 14 && (
                  <button
                    className="hotel-detail-see-all-facilities"
                    onClick={handleOpenFacilitiesModal}
                  >
                    See all facilities
                  </button>
                )}
              </div>
              <div className="hotel-detail-facility-chips">
                {getPopularFacilitiesForMainPage(hotelDetails.hotelFacilities).map((facility, idx) => (
                  <span key={idx} className="hotel-detail-facility-chip">
                    <IndividualFacilityIcon facilityName={facility} />
                    <span className="hotel-detail-facility-name">{facility}</span>
                  </span>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Rooms Section */}
        <div ref={sectionRefs.rooms} className="hotel-detail-section" id="rooms">
          <h3 className="hotel-detail-section-title">Choose your room</h3>
          
          <HotelDetailSearchBar
            variant="section"
            editCheckIn={editCheckIn}
            editCheckOut={editCheckOut}
            editAdults={editAdults}
            editRooms={editRooms}
            onCheckInChange={handleCheckInChange}
            onCheckOutChange={handleCheckOutChange}
            onRoomsGuestsClick={() => {
              setTempAdults(editAdults);
              setTempRooms(editRooms);
              setShowRoomsConfigModal(true);
            }}
            onChangeSearch={handleChangeSearch}
            isSearchChanging={isSearchChanging}
            searchChangeError={searchChangeError}
            onWiderSearch={onWiderSearch}
            isWiderSearchLoading={isWiderSearchLoading}
            hasNoRooms={filteredRooms.length === 0}
          />

          {/* Search Change Error */}
          {searchChangeError && (
            <div className="hotel-detail-search-error">
              {searchChangeError}
            </div>
          )}

          {/* Configuring Rooms Modal */}
          {showRoomsConfigModal && (
            <div className="hotel-detail-rooms-config-overlay" onClick={() => setShowRoomsConfigModal(false)}>
              <div className="hotel-detail-rooms-config-modal" onClick={(e) => e.stopPropagation()}>
                <div className="hotel-detail-rooms-config-header">
                  <span className="hotel-detail-rooms-config-icon">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/>
                      <circle cx="12" cy="7" r="4"/>
                    </svg>
                  </span>
                  <span>Configuring Rooms</span>
                </div>

                <div className="hotel-detail-rooms-config-content">
                  <div className="hotel-detail-rooms-config-section">
                    <span className="hotel-detail-rooms-config-section-title">Room 1</span>
                    
                    <div className="hotel-detail-rooms-config-row">
                      <div className="hotel-detail-rooms-config-label">
                        <span>Adults</span>
                      </div>
                      <div className="hotel-detail-rooms-config-controls">
                        <button
                          type="button"
                          className="hotel-detail-rooms-config-btn"
                          disabled={tempAdults <= 1}
                          onClick={() => setTempAdults(Math.max(1, tempAdults - 1))}
                        >
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <circle cx="12" cy="12" r="10"/>
                            <line x1="8" y1="12" x2="16" y2="12"/>
                          </svg>
                        </button>
                        <span className="hotel-detail-rooms-config-value">{tempAdults}</span>
                        <button
                          type="button"
                          className="hotel-detail-rooms-config-btn"
                          onClick={() => setTempAdults(tempAdults + 1)}
                        >
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <circle cx="12" cy="12" r="10"/>
                            <line x1="12" y1="8" x2="12" y2="16"/>
                            <line x1="8" y1="12" x2="16" y2="12"/>
                          </svg>
                        </button>
                      </div>
                    </div>

                    <div className="hotel-detail-rooms-config-row">
                      <div className="hotel-detail-rooms-config-label">
                        <span>Children</span>
                        <span className="hotel-detail-rooms-config-sublabel">Ages 0 to 17</span>
                      </div>
                      <div className="hotel-detail-rooms-config-controls">
                        <button
                          type="button"
                          className="hotel-detail-rooms-config-btn"
                          disabled={true}
                        >
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <circle cx="12" cy="12" r="10"/>
                            <line x1="8" y1="12" x2="16" y2="12"/>
                          </svg>
                        </button>
                        <span className="hotel-detail-rooms-config-value">0</span>
                        <button
                          type="button"
                          className="hotel-detail-rooms-config-btn"
                          disabled={true}
                        >
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <circle cx="12" cy="12" r="10"/>
                            <line x1="12" y1="8" x2="12" y2="16"/>
                            <line x1="8" y1="12" x2="16" y2="12"/>
                          </svg>
                        </button>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="hotel-detail-rooms-config-footer">
                  <button
                    type="button"
                    className="hotel-detail-rooms-config-add-room"
                    onClick={() => setTempRooms(tempRooms + 1)}
                  >
                    Add room
                  </button>
                  <button
                    type="button"
                    className="hotel-detail-rooms-config-done"
                    onClick={() => {
                      setEditAdults(tempAdults);
                      setEditRooms(tempRooms);
                      setShowRoomsConfigModal(false);
                    }}
                  >
                    Done
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Filters — hidden when no rooms are available */}
          {filteredRooms.length > 0 && (
            <div className="hotel-detail-filters">
              <span className="hotel-detail-filters-label">Filter by:</span>
              <label className="hotel-detail-filter-toggle">
                <input
                  type="checkbox"
                  checked={filters.breakfastIncluded}
                  onChange={(e) => {
                    const checked = e.target.checked;
                    setFilters((prev) => ({ ...prev, breakfastIncluded: checked }));
                    WebAnalytics.track(EVENT_BOOKING_RATE_FILTER_APPLIED, {
                      hotel_id: hotel?.hotelId || null,
                      hotel_name: hotel?.name || null,
                      filter_name: 'breakfastIncluded',
                      filter_value: checked,
                    });
                  }}
                />
                <span className="hotel-detail-filter-toggle-text">Breakfast included</span>
              </label>
              <label className="hotel-detail-filter-toggle">
                <input
                  type="checkbox"
                  checked={filters.refundableOnly}
                  onChange={(e) => {
                    const checked = e.target.checked;
                    setFilters((prev) => ({ ...prev, refundableOnly: checked }));
                    WebAnalytics.track(EVENT_BOOKING_RATE_FILTER_APPLIED, {
                      hotel_id: hotel?.hotelId || null,
                      hotel_name: hotel?.name || null,
                      filter_name: 'refundableOnly',
                      filter_value: checked,
                    });
                  }}
                />
                <span className="hotel-detail-filter-toggle-text">Refundable only</span>
              </label>
            </div>
          )}

          {/* Room List */}
          {filteredRooms.length > 0 ? (
            <div className="hotel-detail-rooms-list">
              {filteredRooms.map((room, roomIdx) => {
                // Use merged room images (already merged from hotel details in filteredRooms)
                const roomImages = room.roomImages || [];
                const currentImageIndex = getRoomImageIndex(room.mappedRoomId);
                
                // Log room image data (first room only)
                if (roomIdx === 0) {
                  log('[HotelDetailView] Room images data', {
                    roomName: room.roomName,
                    mappedRoomId: room.mappedRoomId,
                    roomImagesFromMerged: room.roomImages,
                    roomImagesCount: roomImages.length,
                    hasPhotosFromDetails: roomPhotosMap.has(room.mappedRoomId),
                    photosFromDetails: roomPhotosMap.get(room.mappedRoomId),
                  });
                }
                
                // Get room details from hotelDetails if available
                const roomDetails = hotelDetails?.rooms?.find((r) => r.id === room.mappedRoomId);
                const sleepsCount = room.rates[0]?.adultCount || 2;
                const roomSize = roomDetails?.roomSizeSquare && roomDetails?.roomSizeUnit
                  ? `${roomDetails.roomSizeSquare} ${roomDetails.roomSizeUnit === 'm2' ? 'm²' : roomDetails.roomSizeUnit}`
                  : null;
                
                // Calculate discount percentage: displayPrice (original/SSP) vs price (discounted/total)
                // Formula: discountPercentage = ((displayPrice - price) / displayPrice) * 100
                const calculateDiscount = (rate: HotelRate): number | null => {
                  const { displayPrice, price } = rate;
                  
                  // Log discount calculation
                  log('[HotelDetailView] Discount calculation', {
                    roomName: rate.roomName,
                    displayPrice,
                    price,
                    hasDiscount: displayPrice > price && displayPrice > 0,
                  });
                  
                  if (displayPrice > price && displayPrice > 0) {
                    const discount = Math.round(((displayPrice - price) / displayPrice) * 100);
                    return discount > 0 ? discount : null;
                  }
                  return null;
                };
                
                // Room count prefix like whitelabel (e.g., "1 x Room Name")
                const roomQuantity = 1; // Default to 1, can be enhanced if quantity data is available
                
                return (
                <div key={room.mappedRoomId} className="hotel-detail-room-card-whitelabel">
                  {/* Left Section: Room Details */}
                  <div className="hotel-detail-room-details-section">
                    {/* Room Title with quantity prefix like whitelabel */}
                    <h4 className="hotel-detail-room-title-whitelabel">{roomQuantity} x {room.roomName}</h4>
                    
                    {/* Image Carousel */}
                    <div className="hotel-detail-room-carousel-container">
                      {roomImages.length > 0 ? (
                        <>
                          <div className="hotel-detail-room-carousel" style={{ position: 'relative' }}>
                            <Image 
                              src={roomImages[currentImageIndex]} 
                              alt={room.roomName} 
                              className="hotel-detail-room-carousel-image"
                              fill
                              style={{ objectFit: 'cover' }}
                              onError={(e) => {
                                log('[HotelDetailView] Room image failed to load', {
                                  roomName: room.roomName,
                                  imageUrl: roomImages[currentImageIndex],
                                });
                                (e.target as HTMLImageElement).src = '/images/placeholder-hotel.png';
                              }}
                            />
                            {roomImages.length > 1 && (
                              <>
                                <button
                                  className="hotel-detail-room-carousel-arrow hotel-detail-room-carousel-arrow-left"
                                  onClick={() => setRoomImageIndex(room.mappedRoomId, currentImageIndex === 0 ? roomImages.length - 1 : currentImageIndex - 1)}
                                  aria-label="Previous image"
                                >
                                  ‹
                                </button>
                                <button
                                  className="hotel-detail-room-carousel-arrow hotel-detail-room-carousel-arrow-right"
                                  onClick={() => setRoomImageIndex(room.mappedRoomId, currentImageIndex === roomImages.length - 1 ? 0 : currentImageIndex + 1)}
                                  aria-label="Next image"
                                >
                                  ›
                                </button>
                                <div className="hotel-detail-room-carousel-counter">
                                  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <path d="M8 2C4.68629 2 2 4.68629 2 8C2 11.3137 4.68629 14 8 14C11.3137 14 14 11.3137 14 8C14 4.68629 11.3137 2 8 2ZM8 3C10.7614 3 13 5.23858 13 8C13 10.7614 10.7614 13 8 13C5.23858 13 3 10.7614 3 8C3 5.23858 5.23858 3 8 3Z" fill="currentColor"/>
                                    <path d="M8 4C8.55228 4 9 4.44772 9 5V7H11C11.5523 7 12 7.44772 12 8C12 8.55228 11.5523 9 11 9H9V11C9 11.5523 8.55228 12 8 12C7.44772 12 7 11.5523 7 11V9H5C4.44772 9 4 8.55228 4 8C4 7.44772 4.44772 7 5 7H7V5C7 4.44772 7.44772 4 8 4Z" fill="currentColor"/>
                                  </svg>
                                  <span>{currentImageIndex + 1}/{roomImages.length}</span>
                                </div>
                              </>
                            )}
                          </div>
                        </>
                      ) : (
                        <div className="hotel-detail-room-no-image">
                          <span>No image</span>
                        </div>
                      )}
                    </div>
                    
                    {/* Room Details */}
                    <div className="hotel-detail-room-details-info">
                      <h5 className="hotel-detail-room-details-title">Room details</h5>
                      <div className="hotel-detail-room-details-items">
                        {/* Room Size */}
                        {roomSize && (
                          <div className="hotel-detail-room-detail-item">
                            {/* Floor plan icon */}
                            <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                              <rect x="2" y="2" width="16" height="16" rx="1" stroke="currentColor" strokeWidth="1.5"/>
                              <path d="M2 8H10V18" stroke="currentColor" strokeWidth="1.5"/>
                              <path d="M10 2V8" stroke="currentColor" strokeWidth="1.5"/>
                            </svg>
                            <span>{roomSize}</span>
                          </div>
                        )}
                        {/* Sleeps */}
                        <div className="hotel-detail-room-detail-item">
                          {/* People icon */}
                          <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <circle cx="7" cy="5" r="2.5" stroke="currentColor" strokeWidth="1.5"/>
                            <path d="M2 16C2 12.5 4.5 10 7 10C9.5 10 12 12.5 12 16" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
                            <circle cx="14" cy="6" r="2" stroke="currentColor" strokeWidth="1.5"/>
                            <path d="M18 16C18 13 16 11 14 11C13 11 12.5 11.5 12 12" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
                          </svg>
                          <span>Sleeps {sleepsCount}</span>
                        </div>
                      </div>
                    </div>
                    
                    {/* Amenities */}
                    <div className="hotel-detail-room-amenities">
                      <h5 className="hotel-detail-room-amenities-title">Amenities</h5>
                      <div className="hotel-detail-room-amenities-items">
                        {/* Placeholder amenities - can be enhanced when room-specific amenities are available */}
                        <div className="hotel-detail-room-amenity-item">
                          {/* Telephone receiver icon - matches whitelabel */}
                          <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M3 4.5C3 3.67157 3.67157 3 4.5 3H6.5C7.05228 3 7.5 3.44772 7.5 4V6.5C7.5 7.05228 7.05228 7.5 6.5 7.5H5C5 11.0899 8.91015 15 12.5 15V13.5C12.5 12.9477 12.9477 12.5 13.5 12.5H16C16.5523 12.5 17 12.9477 17 13.5V15.5C17 16.3284 16.3284 17 15.5 17C8.59644 17 3 11.4036 3 4.5Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                          </svg>
                          <span>Telephone</span>
                        </div>
                        <div className="hotel-detail-room-amenity-item">
                          {/* Snowflake icon - matches whitelabel */}
                          <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                            {/* Main vertical line */}
                            <path d="M10 1V19" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round"/>
                            {/* Top branches */}
                            <path d="M10 1L7.5 3.5" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round"/>
                            <path d="M10 1L12.5 3.5" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round"/>
                            {/* Bottom branches */}
                            <path d="M10 19L7.5 16.5" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round"/>
                            <path d="M10 19L12.5 16.5" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round"/>
                            {/* Diagonal line top-left to bottom-right */}
                            <path d="M3.05 3.05L16.95 16.95" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round"/>
                            {/* Branches for diagonal */}
                            <path d="M3.05 3.05L3.05 5.8" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round"/>
                            <path d="M3.05 3.05L5.8 3.05" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round"/>
                            <path d="M16.95 16.95L16.95 14.2" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round"/>
                            <path d="M16.95 16.95L14.2 16.95" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round"/>
                            {/* Diagonal line top-right to bottom-left */}
                            <path d="M16.95 3.05L3.05 16.95" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round"/>
                            {/* Branches for diagonal */}
                            <path d="M16.95 3.05L14.2 3.05" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round"/>
                            <path d="M16.95 3.05L16.95 5.8" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round"/>
                            <path d="M3.05 16.95L5.8 16.95" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round"/>
                            <path d="M3.05 16.95L3.05 14.2" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round"/>
                          </svg>
                          <span>Air conditioning</span>
                        </div>
                      </div>
                      {/* Room details link */}
                      <button
                        className="hotel-detail-room-details-link"
                        onClick={() => {
                          setSelectedRoomForDetails(room);
                          setRoomDetailsModalImageIndex(0);
                          setShowRoomDetailsModal(true);
                        }}
                      >
                        Room details
                      </button>
                    </div>
                  </div>
                  
                  {/* Right Section: Booking Options - 3 Column Layout */}
                  <div className="hotel-detail-room-booking-options">
                    {room.rates.map((rate, idx) => {
                      const discount = calculateDiscount(rate);
                      // Sum only taxes with included === false (additional fees paid at property)
                      const totalTaxes = rate.taxesAndFees?.filter(t => t.included === false).reduce((sum, t) => sum + t.amount, 0) || 0;
                      
                      return (
                        <div key={`${rate.rateId}-${idx}`} className="hotel-detail-booking-option-row">
                          {/* Column 2: Room Information */}
                          <div className="hotel-detail-booking-option-info-col">
                            <h5 className="hotel-detail-booking-option-title">
                              {rate.breakfastIncluded ? 'Room with Breakfast' : 'Room Only'}
                            </h5>
                            <div className="hotel-detail-booking-option-details">
                              {rate.breakfastIncluded ? (
                                <div className="hotel-detail-booking-option-detail-item hotel-detail-booking-option-detail-item-check">
                                  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <path d="M3 8L6.5 11.5L13 5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                                  </svg>
                                  <span>Breakfast</span>
                                </div>
                              ) : (
                                <div className="hotel-detail-booking-option-detail-item">
                                  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <circle cx="8" cy="8" r="6" stroke="currentColor" strokeWidth="1.5"/>
                                    <path d="M5 8H11" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
                                  </svg>
                                  <span>No meals included</span>
                                </div>
                              )}
                              {rate.isRefundable ? (() => {
                                const deadline = getFreeCancelDeadline(rate.cancelPolicyInfos);
                                return (
                                  <div
                                    className="hotel-detail-booking-option-detail-item hotel-detail-booking-option-detail-item-check"
                                    style={deadline ? { alignItems: 'flex-start' } : undefined}
                                  >
                                    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ flexShrink: 0, marginTop: deadline ? 2 : 0 }}>
                                      <path d="M3 8L6.5 11.5L13 5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                                    </svg>
                                    <span style={{ color: '#36a832' }}>
                                      Free cancellation
                                      {deadline && <><br /><span>{deadline}</span></>}
                                    </span>
                                  </div>
                                );
                              })() : (
                                <div className="hotel-detail-booking-option-detail-item">
                                  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                                    <circle cx="8" cy="8" r="6" stroke="currentColor" strokeWidth="1.5"/>
                                    <circle cx="8" cy="6" r="1" fill="currentColor"/>
                                    <path d="M8 8V12" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
                                  </svg>
                                  <span>Non-Refundable</span>
                                </div>
                              )}
                            </div>
                          </div>
                          
                          {/* Column 3: Price Details */}
                          <div className="hotel-detail-booking-option-price-col">
                            {discount && (
                              <div className="hotel-detail-booking-option-discount">
                                {discount}% OFF
                              </div>
                            )}
                            <div className="hotel-detail-booking-option-price-main">
                              <span className="hotel-detail-booking-option-price">
                                {formatPrice(Math.ceil(rate.price / nights), rate.currency)}/night
                              </span>
                              {/* Original price (struck through) - shown when displayPrice > price (discount available) */}
                              {rate.displayPrice > rate.price && (
                                <span className="hotel-detail-booking-option-price-original">
                                  {formatPrice(Math.ceil(rate.displayPrice / nights), rate.currency)}
                                </span>
                              )}
                            </div>
                            <div className="hotel-detail-booking-option-price-total">
                              {formatPrice(Math.ceil(rate.price), rate.currency)} Total
                              {totalTaxes === 0 && (
                                <span className="hotel-detail-booking-option-price-all-included"> (All Included)</span>
                              )}
                            </div>
                            <div className="hotel-detail-booking-option-price-details">
                              ({nights} {nights === 1 ? 'night' : 'nights'}, {rooms} {rooms === 1 ? 'Room' : 'Rooms'} {totalTaxes > 0 ? `(+${formatPrice(Math.ceil(totalTaxes), rate.currency)} taxes and fees)` : 'incl. taxes'})
                            </div>
                          </div>
                          
                          {/* Column 4: Choose Room Button */}
                          <div className="hotel-detail-booking-option-button-col">
                            <button
                              className="hotel-detail-booking-option-button"
                              onClick={() => handleRateSelect(room, rate)}
                            >
                              Choose room
                            </button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
              })}
            </div>
          ) : (
            <div className="hotel-detail-no-rooms-banner">
              <div className="hotel-detail-no-rooms-icon">
                <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <rect x="3" y="4" width="14" height="13" rx="2" stroke="currentColor" strokeWidth="1.5"/>
                  <path d="M3 8H17" stroke="currentColor" strokeWidth="1.5"/>
                  <path d="M7 2V6M13 2V6" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
                  <path d="M4 4L16 16" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
                </svg>
              </div>
              <div className="hotel-detail-no-rooms-content">
                <p className="hotel-detail-no-rooms-title">
                  {filters.breakfastIncluded || filters.refundableOnly
                    ? 'No rooms match your filters'
                    : 'This hotel is fully booked for your selected dates'}
                </p>
                <p className="hotel-detail-no-rooms-subtitle">
                  {filters.breakfastIncluded || filters.refundableOnly
                    ? 'Try clearing your filters or adjusting your dates'
                    : 'Please adjust your dates to see if this hotel becomes available'}
                </p>
              </div>
              {(filters.breakfastIncluded || filters.refundableOnly) && (
                <button
                  className="hotel-detail-clear-filters-banner"
                  onClick={() => setFilters({ breakfastIncluded: false, refundableOnly: false })}
                >
                  Clear filters
                </button>
              )}
            </div>
          )}
        </div>

        {/* Reviews Section */}
        <div ref={sectionRefs.reviews} className="hotel-detail-section" id="reviews">
          <h3 className="hotel-detail-section-title">Guest reviews</h3>
          
          {/* Two-column layout like modal */}
          <div className="hotel-detail-section-two-column">
            {/* Left Column - Rating, Categories, Demographics */}
            <div className="hotel-detail-section-left-column">
              {/* Rating Summary - use same rating/count as Review highlights (hotelDetails) */}
              {(rating || reviewCount > 0) && (
                <div className="hotel-detail-section-rating-row">
                  {rating && (
                    <div 
                      className="hotel-detail-section-rating-circle"
                      style={{ backgroundColor: getRatingInfo(rating).color }}
                    >
                      <span className="hotel-detail-section-rating-score">{rating.toFixed(1)}</span>
                    </div>
                  )}
                  <div className="hotel-detail-section-rating-text">
                    {rating && (
                      <span className="hotel-detail-section-rating-label">
                        {getRatingInfo(rating).label}
                      </span>
                    )}
                    <span className="hotel-detail-section-rating-count">
                      Based on {reviewCount.toLocaleString()} reviews
                    </span>
                  </div>
                </div>
              )}

              {/* Top Comments by Category */}
              <div className="hotel-detail-section-categories">
                <h4 className="hotel-detail-section-categories-title">Top comments from travelers</h4>
                <div className="hotel-detail-section-category-pills">
                  <span className="hotel-detail-section-category-pill">Cleanliness (7.1)</span>
                  <span className="hotel-detail-section-category-pill">Service (8.5)</span>
                  <span className="hotel-detail-section-category-pill">Location (9.1)</span>
                  <span className="hotel-detail-section-category-pill">Room Quality (7.5)</span>
                  <span className="hotel-detail-section-category-pill">Amenities (7)</span>
                  <span className="hotel-detail-section-category-pill">Value for Money (7.5)</span>
                  <span className="hotel-detail-section-category-pill">Food and Beverage (8.1)</span>
                </div>
              </div>

              {/* Who Stays Here */}
              <div className="hotel-detail-section-demographics">
                <h4 className="hotel-detail-section-demographics-title">Who stays here</h4>
                <div className="hotel-detail-section-demographics-grid">
                  <div className="hotel-detail-section-demographic-item">
                    <svg className="hotel-detail-section-demographic-icon" width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M9 11C11.2091 11 13 9.20914 13 7C13 4.79086 11.2091 3 9 3C6.79086 3 5 4.79086 5 7C5 9.20914 6.79086 11 9 11Z" stroke="currentColor" strokeWidth="1.5"/>
                      <path d="M3 21V19C3 16.7909 4.79086 15 7 15H11C13.2091 15 15 16.7909 15 19V21" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
                      <path d="M16 3C17.6569 3.47656 19 5.07875 19 7C19 8.92125 17.6569 10.5234 16 11" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
                      <path d="M17 15C19.2091 15.0001 21 16.7909 21 19V21" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
                    </svg>
                    <span className="hotel-detail-section-demographic-label">Family</span>
                    <span className="hotel-detail-section-demographic-percent">41%</span>
                  </div>
                  <div className="hotel-detail-section-demographic-item">
                    <svg className="hotel-detail-section-demographic-icon" width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M12 21.35L10.55 20.03C5.4 15.36 2 12.28 2 8.5C2 5.42 4.42 3 7.5 3C9.24 3 10.91 3.81 12 5.09C13.09 3.81 14.76 3 16.5 3C19.58 3 22 5.42 22 8.5C22 12.28 18.6 15.36 13.45 20.04L12 21.35Z" stroke="currentColor" strokeWidth="1.5"/>
                    </svg>
                    <span className="hotel-detail-section-demographic-label">Couple</span>
                    <span className="hotel-detail-section-demographic-percent">40%</span>
                  </div>
                  <div className="hotel-detail-section-demographic-item">
                    <svg className="hotel-detail-section-demographic-icon" width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <circle cx="12" cy="7" r="4" stroke="currentColor" strokeWidth="1.5"/>
                      <path d="M6 21V19C6 16.7909 7.79086 15 10 15H14C16.2091 15 18 16.7909 18 19V21" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
                    </svg>
                    <span className="hotel-detail-section-demographic-label">Solo</span>
                    <span className="hotel-detail-section-demographic-percent">11%</span>
                  </div>
                  <div className="hotel-detail-section-demographic-item">
                    <svg className="hotel-detail-section-demographic-icon" width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <circle cx="9" cy="7" r="3" stroke="currentColor" strokeWidth="1.5"/>
                      <circle cx="15" cy="7" r="3" stroke="currentColor" strokeWidth="1.5"/>
                      <path d="M3 21V19C3 17.3431 4.34315 16 6 16H12C13.6569 16 15 17.3431 15 19V21" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
                      <path d="M15 16H18C19.6569 16 21 17.3431 21 19V21" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
                    </svg>
                    <span className="hotel-detail-section-demographic-label">Friends/Group</span>
                    <span className="hotel-detail-section-demographic-percent">8%</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Right Column - Reviews List */}
            <div className="hotel-detail-section-right-column">
              {/* Sort Controls */}
              <div className="hotel-detail-section-sort">
                <select 
                  className="hotel-detail-section-sort-select"
                  value={reviewSort}
                  onChange={(e) => handleReviewSortChange(e.target.value as ReviewSortOption)}
                  disabled={isLoadingReviews}
                >
                  <option value="newest">Newest first</option>
                  <option value="highest">Highest rated</option>
                  <option value="lowest">Lowest rated</option>
                </select>
              </div>

              {/* Reviews List - scrollable */}
              {reviews.length > 0 ? (
                <div className="hotel-detail-section-reviews-list">
                {reviews.map((review) => (
                  <div key={review.id} className="hotel-detail-section-review-card">
                    <div className="hotel-detail-section-review-header">
                      <div className="hotel-detail-section-reviewer-info">
                        <div className="hotel-detail-section-reviewer-avatar">
                          {(review.reviewerName || 'Guest').charAt(0).toUpperCase()}
                        </div>
                        <div className="hotel-detail-section-reviewer-details">
                          <span className="hotel-detail-section-reviewer-name">{review.reviewerName || 'Guest'}</span>
                          <div className="hotel-detail-section-reviewer-meta">
                            {review.travelerType && (
                              <span className="hotel-detail-section-traveler-type">{review.travelerType}</span>
                            )}
                            {review.date && (
                              <span className="hotel-detail-section-review-date">
                                {new Date(review.date).toLocaleDateString('en-US', { weekday: 'short', day: 'numeric', month: 'short' })}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                      {review.rating != null && (
                        <div 
                          className="hotel-detail-section-review-rating"
                          style={{ backgroundColor: getRatingInfo(review.rating).color }}
                        >
                          <span>{review.rating.toFixed(0)}</span>
                        </div>
                      )}
                    </div>
                    
                    {review.title && (
                      <h4 className="hotel-detail-section-review-title">{review.title}</h4>
                    )}
                    
                    {review.positiveText && (
                      <div className="hotel-detail-section-review-feedback positive">
                        <span className="hotel-detail-section-feedback-icon">👍</span>
                        <p>{review.positiveText}</p>
                      </div>
                    )}
                    
                    {review.negativeText && (
                      <div className="hotel-detail-section-review-feedback negative">
                        <span className="hotel-detail-section-feedback-icon">👎</span>
                        <p>{review.negativeText}</p>
                      </div>
                    )}
                    
                    {review.text && !review.positiveText && !review.negativeText && (
                      <p className="hotel-detail-section-review-text">
                        {review.text.trim() || <span style={{ fontStyle: 'italic', color: '#999' }}>No review text available</span>}
                      </p>
                    )}
                  </div>
                ))}
                
                {/* Load more + review count: X = loaded reviews (accumulated), Y = total from hotelDetails (constant) */}
                  <div className="hotel-detail-section-reviews-footer">
                    {hasMoreReviews && (
                      <button
                        className="hotel-detail-section-load-more"
                        onClick={() => fetchReviews(reviewsPage + 1)}
                        disabled={isLoadingReviews}
                      >
                        {isLoadingReviews ? (
                          <>
                            <span className="hotel-detail-loading-spinner" />
                            Loading...
                          </>
                        ) : (
                          'Load more reviews'
                        )}
                      </button>
                    )}
                    <span className="hotel-detail-section-reviews-count">
                      Showing {reviews.length.toLocaleString()} of {reviewCount.toLocaleString()} reviews
                    </span>
                  </div>
                </div>
              ) : isLoadingReviews ? (
                <div className="hotel-detail-reviews-loading">
                  <span className="hotel-detail-loading-spinner large" />
                  <p>Loading reviews...</p>
                </div>
              ) : (
                <div className="hotel-detail-reviews-empty">
                  <span className="hotel-detail-reviews-empty-icon">💬</span>
                  <p>No reviews available</p>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Description Section */}
        <div ref={sectionRefs.description} className="hotel-detail-section" id="description">
          {/* Description Text - simple style like whitelabel */}
          {hotelDetails?.hotelDescription ? (
            <div className="hotel-detail-description-simple">
              <h3 className="hotel-detail-description-title">Property Description</h3>
              <div dangerouslySetInnerHTML={{ __html: hotelDetails.hotelDescription }} />
            </div>
          ) : (
            <div className="hotel-detail-description-simple">
              <h3 className="hotel-detail-description-title">Property Description</h3>
              <p className="hotel-detail-no-data">No description available</p>
            </div>
          )}
        </div>

        {/* Footer */}
        <footer className="hotel-detail-footer">
          <div className="hotel-detail-footer-logo">
            <Image 
              src={logoIcon} 
              alt="Globe Thrivers" 
              width={120}
              height={32}
              style={{ objectFit: 'contain' }}
            />
          </div>
          <div className="hotel-detail-footer-links">
            <button 
              className="hotel-detail-footer-link"
              onClick={() => setShowTermsModal(true)}
            >
              Terms & Conditions
            </button>
            <button 
              className="hotel-detail-footer-link"
              onClick={() => setShowPrivacyModal(true)}
            >
              Privacy Policy
            </button>
            <button 
              className="hotel-detail-footer-link"
              onClick={() => setShowContactModal(true)}
            >
              Contact us
            </button>
          </div>
        </footer>
      </div>

      {/* Full Image Gallery Modal */}
      {showAllImages && allImages.length > 0 && (
        <div className="hotel-detail-gallery-modal" onClick={() => setShowAllImages(false)}>
          <div className="hotel-detail-gallery-modal-content" onClick={(e) => e.stopPropagation()}>
            <button className="hotel-detail-gallery-close" onClick={() => setShowAllImages(false)}>×</button>
            <div className="hotel-detail-gallery-main" style={{ position: 'relative' }}>
              <Image src={allImages[currentImageIndex]} alt={displayName} fill style={{ objectFit: 'cover' }} />
            </div>
            <div className="hotel-detail-gallery-thumbnails">
              {allImages.map((img, idx) => (
                <div
                  key={idx}
                  className={`hotel-detail-gallery-thumb ${currentImageIndex === idx ? 'active' : ''}`}
                  onClick={() => setCurrentImageIndex(idx)}
                  style={{ position: 'relative' }}
                >
                  <Image src={img} alt={`${displayName} ${idx + 1}`} fill style={{ objectFit: 'cover' }} />
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Facilities Modal */}
      {showFacilitiesModal && hotelDetails?.hotelFacilities && hotelDetails.hotelFacilities.length > 0 && (
        <div className="hotel-detail-facilities-modal-overlay" onClick={() => setShowFacilitiesModal(false)}>
          <div className="hotel-detail-facilities-modal" onClick={(e) => e.stopPropagation()}>
            <div className="hotel-detail-facilities-modal-header">
              <h3 className="hotel-detail-facilities-modal-title">Facilities</h3>
              <button 
                className="hotel-detail-facilities-modal-close"
                onClick={() => setShowFacilitiesModal(false)}
                aria-label="Close facilities modal"
              >
                ×
              </button>
            </div>
            <div className="hotel-detail-facilities-modal-content">
              {/* Popular facilities section (first 5 filtered facilities with icons) */}
              <div className="hotel-detail-facilities-modal-popular">
                <h4 className="hotel-detail-facilities-modal-popular-title">Popular facilities</h4>
                <div className="hotel-detail-facilities-modal-popular-grid">
                  {getPopularFacilitiesForModal(hotelDetails.hotelFacilities).map((facility, idx) => (
                    <div key={idx} className="hotel-detail-facilities-modal-popular-item">
                      <IndividualFacilityIcon facilityName={facility} />
                      <span>{facility}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Categorized facilities */}
              {Array.from(categorizeFacilities(hotelDetails.hotelFacilities).entries()).map(([categoryKey, facilities]) => {
                const categoryInfo = FACILITY_CATEGORIES[categoryKey] || { label: 'Other', category: 'other' };
                return (
                  <div key={categoryKey} className="hotel-detail-facilities-modal-category">
                    <h4 className="hotel-detail-facilities-modal-category-title">
                      <span className="hotel-detail-facilities-modal-category-icon">
                        <FacilityIcon category={categoryInfo.category} />
                      </span>
                      {categoryInfo.label}
                    </h4>
                    <ul className="hotel-detail-facilities-modal-category-list">
                      {facilities.map((facility, idx) => (
                        <li key={idx} className="hotel-detail-facilities-modal-item">
                          {facility}
                        </li>
                      ))}
                    </ul>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {/* Reviews Modal */}
      {showReviewsModal && (
        <div className="hotel-detail-reviews-modal-overlay" onClick={() => setShowReviewsModal(false)}>
          <div className="hotel-detail-reviews-modal" onClick={(e) => e.stopPropagation()}>
            {(() => {
              log('[HotelDetailView] Rendering reviews modal', {
                showReviewsModal,
                reviewsLength: reviews.length,
                hasReviewSummary: !!reviewSummary,
                reviewSummary,
                isLoadingReviews,
                reviewCount,
                rating,
              });
              return null;
            })()}
            <div className="hotel-detail-reviews-modal-header">
              <h3 className="hotel-detail-reviews-modal-title">Guest reviews</h3>
              <button 
                className="hotel-detail-reviews-modal-close"
                onClick={() => setShowReviewsModal(false)}
                aria-label="Close reviews modal"
              >
                ×
              </button>
            </div>
            <div className="hotel-detail-reviews-modal-content">
              {/* Two-column layout */}
              <div className="hotel-detail-reviews-modal-two-column">
                {/* Left Column - Rating, Categories, Demographics */}
                <div className="hotel-detail-reviews-modal-left-column">
                  {/* Overall Rating */}
                  {(() => {
                    log('[HotelDetailView] Rendering rating section in modal', {
                      hasReviewSummary: !!reviewSummary,
                      overallScore: reviewSummary?.overallScore,
                      reviewCount: reviewSummary?.reviewCount,
                      rating: hotelDetails?.rating,
                      reviewCountFromHotel: hotelDetails?.reviewCount,
                      reviewSummaryFull: reviewSummary,
                    });
                    return null;
                  })()}
                  {(reviewSummary || hotelDetails?.rating) && (
                    <div className="hotel-detail-reviews-modal-rating-section">
                      {(() => {
                        // Use hotelDetails.rating as primary source (from hotel details endpoint)
                        const displayScore = hotelDetails?.rating;
                        const displayCount = hotelDetails?.reviewCount || reviewSummary?.reviewCount || 0;
                        
                        if (displayScore !== null && displayScore !== undefined) {
                          const ratingInfo = getRatingInfo(displayScore);
                          return (
                            <div className="hotel-detail-reviews-modal-rating-row">
                              <div 
                                className="hotel-detail-reviews-modal-rating-circle"
                                style={{ backgroundColor: ratingInfo.color }}
                              >
                                <span className="hotel-detail-reviews-modal-rating-score">{displayScore.toFixed(1)}</span>
                              </div>
                              <div className="hotel-detail-reviews-modal-rating-text">
                                <span className="hotel-detail-reviews-modal-rating-label">
                                  {ratingInfo.label}
                                </span>
                                <span className="hotel-detail-reviews-modal-rating-count">
                                  Based on {displayCount.toLocaleString()} reviews
                                </span>
                              </div>
                            </div>
                          );
                        }
                        return (
                          <div className="hotel-detail-reviews-modal-rating-circle">
                            <span className="hotel-detail-reviews-modal-no-rating">No ratings yet</span>
                          </div>
                        );
                      })()}
                    </div>
                  )}

                  {/* Top Comments by Category */}
                  <div className="hotel-detail-reviews-modal-categories">
                    <h4 className="hotel-detail-reviews-modal-categories-title">Top comments from travelers</h4>
                    <div className="hotel-detail-reviews-modal-category-pills">
                      <span className="hotel-detail-reviews-modal-category-pill">Cleanliness (7.1)</span>
                      <span className="hotel-detail-reviews-modal-category-pill">Service (8.5)</span>
                      <span className="hotel-detail-reviews-modal-category-pill">Location (9.1)</span>
                      <span className="hotel-detail-reviews-modal-category-pill">Room Quality (7.5)</span>
                      <span className="hotel-detail-reviews-modal-category-pill">Amenities (7)</span>
                      <span className="hotel-detail-reviews-modal-category-pill">Value for Money (7.5)</span>
                      <span className="hotel-detail-reviews-modal-category-pill">Food and Beverage (8.1)</span>
                    </div>
                  </div>

                  {/* Who Stays Here */}
                  <div className="hotel-detail-reviews-modal-demographics">
                    <h4 className="hotel-detail-reviews-modal-demographics-title">Who stays here</h4>
                    <div className="hotel-detail-reviews-modal-demographics-grid">
                      <div className="hotel-detail-reviews-modal-demographic-item">
                        <svg className="hotel-detail-reviews-modal-demographic-icon" width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path d="M9 11C11.2091 11 13 9.20914 13 7C13 4.79086 11.2091 3 9 3C6.79086 3 5 4.79086 5 7C5 9.20914 6.79086 11 9 11Z" stroke="currentColor" strokeWidth="1.5"/>
                          <path d="M3 21V19C3 16.7909 4.79086 15 7 15H11C13.2091 15 15 16.7909 15 19V21" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
                          <path d="M16 3C17.6569 3.47656 19 5.07875 19 7C19 8.92125 17.6569 10.5234 16 11" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
                          <path d="M17 15C19.2091 15.0001 21 16.7909 21 19V21" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
                        </svg>
                        <span className="hotel-detail-reviews-modal-demographic-label">Family</span>
                        <span className="hotel-detail-reviews-modal-demographic-percent">41%</span>
                      </div>
                      <div className="hotel-detail-reviews-modal-demographic-item">
                        <svg className="hotel-detail-reviews-modal-demographic-icon" width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path d="M12 21.35L10.55 20.03C5.4 15.36 2 12.28 2 8.5C2 5.42 4.42 3 7.5 3C9.24 3 10.91 3.81 12 5.09C13.09 3.81 14.76 3 16.5 3C19.58 3 22 5.42 22 8.5C22 12.28 18.6 15.36 13.45 20.04L12 21.35Z" stroke="currentColor" strokeWidth="1.5"/>
                        </svg>
                        <span className="hotel-detail-reviews-modal-demographic-label">Couple</span>
                        <span className="hotel-detail-reviews-modal-demographic-percent">40%</span>
                      </div>
                      <div className="hotel-detail-reviews-modal-demographic-item">
                        <svg className="hotel-detail-reviews-modal-demographic-icon" width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <circle cx="12" cy="7" r="4" stroke="currentColor" strokeWidth="1.5"/>
                          <path d="M6 21V19C6 16.7909 7.79086 15 10 15H14C16.2091 15 18 16.7909 18 19V21" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
                        </svg>
                        <span className="hotel-detail-reviews-modal-demographic-label">Solo</span>
                        <span className="hotel-detail-reviews-modal-demographic-percent">11%</span>
                      </div>
                      <div className="hotel-detail-reviews-modal-demographic-item">
                        <svg className="hotel-detail-reviews-modal-demographic-icon" width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <circle cx="9" cy="7" r="3" stroke="currentColor" strokeWidth="1.5"/>
                          <circle cx="15" cy="7" r="3" stroke="currentColor" strokeWidth="1.5"/>
                          <path d="M3 21V19C3 17.3431 4.34315 16 6 16H12C13.6569 16 15 17.3431 15 19V21" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
                          <path d="M15 16H18C19.6569 16 21 17.3431 21 19V21" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
                        </svg>
                        <span className="hotel-detail-reviews-modal-demographic-label">Friends/Group</span>
                        <span className="hotel-detail-reviews-modal-demographic-percent">8%</span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Right Column - Reviews List */}
                <div className="hotel-detail-reviews-modal-right-column">
                  {/* Sort Controls */}
                  <div className="hotel-detail-reviews-modal-sort">
                    <select 
                      className="hotel-detail-reviews-modal-sort-select"
                      value={reviewSort}
                      onChange={(e) => handleReviewSortChange(e.target.value as ReviewSortOption)}
                      disabled={isLoadingReviews}
                    >
                      <option value="newest">Newest first</option>
                      <option value="highest">Highest rated</option>
                      <option value="lowest">Lowest rated</option>
                    </select>
                  </div>

                  {/* Reviews List with Lazy Loading - Fixed height scrollable */}
                  <div className="hotel-detail-reviews-modal-list">
                {(() => {
                  log('[HotelDetailView] Rendering reviews list in modal', {
                    reviewsLength: reviews.length,
                    isLoadingReviews,
                    hasMoreReviews,
                    reviewsPage,
                    reviewCount: reviewSummary?.reviewCount || 0,
                    hotelReviewCount: hotelDetails?.reviewCount || 0,
                    reviews: reviews.slice(0, 3).map(r => ({
                      id: r.id,
                      reviewerName: r.reviewerName,
                      rating: r.rating,
                      hasText: !!r.text,
                      textLength: r.text?.length || 0,
                      textPreview: r.text?.substring(0, 50) || 'NO TEXT',
                      hasDate: !!r.date,
                      date: r.date,
                      hasTitle: !!r.title,
                      title: r.title,
                      fullReview: r,
                    })),
                  });
                  return null;
                })()}
                {reviews.length > 0 ? (
                  <>
                    {reviews.map((review) => (
                      <div key={review.id} className="hotel-detail-reviews-modal-review-card">
                        <div className="hotel-detail-reviews-modal-review-header">
                          <div className="hotel-detail-reviews-modal-reviewer-info">
                            <div className="hotel-detail-reviews-modal-reviewer-avatar">
                              {(review.reviewerName || 'Guest').charAt(0).toUpperCase()}
                            </div>
                            <div className="hotel-detail-reviews-modal-reviewer-details">
                              <span className="hotel-detail-reviews-modal-reviewer-name">{review.reviewerName || 'Guest'}</span>
                              <div className="hotel-detail-reviews-modal-reviewer-meta">
                                {review.travelerType && (
                                  <span className="hotel-detail-reviews-modal-traveler-type">{review.travelerType}</span>
                                )}
                                {review.date && (
                                  <span className="hotel-detail-reviews-modal-review-date">
                                    {new Date(review.date).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}
                                  </span>
                                )}
                              </div>
                            </div>
                          </div>
                          {review.rating != null && (
                            <div 
                              className="hotel-detail-reviews-modal-review-rating"
                              style={{ backgroundColor: getRatingInfo(review.rating).color }}
                            >
                              <span>{review.rating.toFixed(0)}</span>
                            </div>
                          )}
                        </div>
                        
                        {review.title && (
                          <h4 className="hotel-detail-reviews-modal-review-title">{review.title}</h4>
                        )}
                        
                        {review.text && review.text.trim() ? (
                          <p className="hotel-detail-reviews-modal-review-text">{review.text}</p>
                        ) : (
                          <p className="hotel-detail-reviews-modal-review-text" style={{ fontStyle: 'italic', color: '#999' }}>
                            No review text available
                          </p>
                        )}
                      </div>
                    ))}
                    
                    {/* Load More Button */}
                    {hasMoreReviews && (
                      <button
                        className="hotel-detail-reviews-modal-load-more"
                        onClick={() => fetchReviews(reviewsPage + 1)}
                        disabled={isLoadingReviews}
                      >
                        {isLoadingReviews ? (
                          <>
                            <span className="hotel-detail-loading-spinner" />
                            Loading...
                          </>
                        ) : (
                          'Load more reviews'
                        )}
                      </button>
                    )}
                    
                    {/* Showing count: X = loaded reviews (accumulated), Y = total from hotelDetails (constant) */}
                    <div className="hotel-detail-reviews-modal-showing">
                      Showing {reviews.length.toLocaleString()} of {reviewCount.toLocaleString()} reviews
                    </div>
                  </>
                ) : isLoadingReviews ? (
                  <div className="hotel-detail-reviews-modal-loading">
                    <span className="hotel-detail-loading-spinner large" />
                    <p>Loading reviews...</p>
                  </div>
                ) : (reviewSummary?.reviewCount || 0) > 0 ? (
                  <div className="hotel-detail-reviews-modal-empty">
                    <p>Reviews temporarily unavailable</p>
                    <p className="hotel-detail-reviews-modal-empty-subtitle">
                      This hotel has {(reviewSummary?.reviewCount || 0).toLocaleString()} review{(reviewSummary?.reviewCount || 0) !== 1 ? 's' : ''}, but they are not currently available.
                    </p>
                  </div>
                ) : (
                  <div className="hotel-detail-reviews-modal-empty">
                    <p>No reviews available</p>
                  </div>
                )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Room Details Modal */}
      {showRoomDetailsModal && selectedRoomForDetails && roomDetailsModalData && (
          <div className="hotel-detail-room-details-modal-overlay" onClick={() => setShowRoomDetailsModal(false)}>
            <div className="hotel-detail-room-details-modal" onClick={(e) => e.stopPropagation()}>
              <div className="hotel-detail-room-details-modal-header">
                <h3 className="hotel-detail-room-details-modal-title">Room details</h3>
                <button
                  className="hotel-detail-room-details-modal-close"
                  onClick={() => setShowRoomDetailsModal(false)}
                  aria-label="Close room details modal"
                >
                  ×
                </button>
              </div>
              <div className="hotel-detail-room-details-modal-content">
                {/* Room Image Carousel */}
                {roomDetailsModalData.roomImages.length > 0 && (
                  <div className="hotel-detail-room-details-modal-image-section">
                    <div className="hotel-detail-room-details-modal-carousel" style={{ position: 'relative' }}>
                      <Image
                        src={roomDetailsModalData.roomImages[roomDetailsModalImageIndex]}
                        alt={selectedRoomForDetails.roomName}
                        className="hotel-detail-room-details-modal-image"
                        fill
                        style={{ objectFit: 'cover' }}
                        onError={(e) => {
                          (e.target as HTMLImageElement).src = '/images/placeholder-hotel.png';
                        }}
                      />
                      {roomDetailsModalData.roomImages.length > 1 && (
                        <>
                          <button
                            className="hotel-detail-room-details-modal-arrow hotel-detail-room-details-modal-arrow-left"
                            onClick={(e) => {
                              e.stopPropagation();
                              setRoomDetailsModalImageIndex((prev) => (prev === 0 ? roomDetailsModalData.roomImages.length - 1 : prev - 1));
                            }}
                            aria-label="Previous image"
                          >
                            ‹
                          </button>
                          <button
                            className="hotel-detail-room-details-modal-arrow hotel-detail-room-details-modal-arrow-right"
                            onClick={(e) => {
                              e.stopPropagation();
                              setRoomDetailsModalImageIndex((prev) => (prev === roomDetailsModalData.roomImages.length - 1 ? 0 : prev + 1));
                            }}
                            aria-label="Next image"
                          >
                            ›
                          </button>
                          <div className="hotel-detail-room-details-modal-counter">
                            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                              <path d="M8 2C4.68629 2 2 4.68629 2 8C2 11.3137 4.68629 14 8 14C11.3137 14 14 11.3137 14 8C14 4.68629 11.3137 2 8 2ZM8 3C10.7614 3 13 5.23858 13 8C13 10.7614 10.7614 13 8 13C5.23858 13 3 10.7614 3 8C3 5.23858 5.23858 3 8 3Z" fill="currentColor"/>
                              <path d="M8 4C8.55228 4 9 4.44772 9 5V7H11C11.5523 7 12 7.44772 12 8C12 8.55228 11.5523 9 11 9H9V11C9 11.5523 8.55228 12 8 12C7.44772 12 7 11.5523 7 11V9H5C4.44772 9 4 8.55228 4 8C4 7.44772 4.44772 7 5 7H7V5C7 4.44772 7.44772 4 8 4Z" fill="currentColor"/>
                            </svg>
                            <span>{roomDetailsModalImageIndex + 1}/{roomDetailsModalData.roomImages.length}</span>
                          </div>
                        </>
                      )}
                    </div>
                  </div>
                )}

                {/* Room Type */}
                <h4 className="hotel-detail-room-details-modal-room-type">{selectedRoomForDetails.roomName}</h4>

                {/* Key Metrics */}
                <div className="hotel-detail-room-details-modal-metrics">
                  {roomDetailsModalData.roomSize && (
                    <div className="hotel-detail-room-details-modal-metric">
                      <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <rect x="3" y="4" width="14" height="12" rx="1" stroke="currentColor" strokeWidth="1.5"/>
                        <path d="M3 7H17" stroke="currentColor" strokeWidth="1.5"/>
                        <path d="M7 4V7" stroke="currentColor" strokeWidth="1.5"/>
                        <path d="M13 4V7" stroke="currentColor" strokeWidth="1.5"/>
                      </svg>
                      <span>{roomDetailsModalData.roomSize}</span>
                    </div>
                  )}
                  <div className="hotel-detail-room-details-modal-metric">
                    <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <circle cx="7" cy="6" r="2.5" stroke="currentColor" strokeWidth="1.5"/>
                      <circle cx="13" cy="6" r="2.5" stroke="currentColor" strokeWidth="1.5"/>
                      <path d="M3 15C3 12 5 10 7 10C7.5 10 7.75 10.25 8 10.5C8.25 10.25 8.5 10 9 10C11 10 13 12 13 15" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
                    </svg>
                    <span>Sleeps {roomDetailsModalData.occupancy}</span>
                  </div>
                </div>

                {/* Room Description */}
                {roomDetailsModalData.roomDetailsData?.description && (
                  <div className="hotel-detail-room-details-modal-section">
                    <p 
                      className="hotel-detail-room-details-modal-description"
                      dangerouslySetInnerHTML={{ __html: roomDetailsModalData.roomDetailsData.description }}
                    />
                  </div>
                )}

                {/* Popular Amenities */}
                {roomDetailsModalData.popularAmenities.length > 0 && (
                  <div className="hotel-detail-room-details-modal-section">
                    <h5 className="hotel-detail-room-details-modal-section-title">Popular amenities</h5>
                    <div className="hotel-detail-room-details-modal-amenities-grid">
                      {roomDetailsModalData.popularAmenities.map((amenity, idx) => (
                        <div key={idx} className="hotel-detail-room-details-modal-amenity">
                          <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <circle cx="10" cy="10" r="8" stroke="currentColor" strokeWidth="1.5"/>
                            <path d="M7 10L9 12L13 8" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                          </svg>
                          <span>{amenity}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* General Amenities */}
                {roomDetailsModalData.generalAmenities.length > 0 && (
                  <div className="hotel-detail-room-details-modal-section">
                    <h5 className="hotel-detail-room-details-modal-section-title-with-icon">
                      <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <rect x="3" y="3" width="6" height="6" rx="1" stroke="currentColor" strokeWidth="1.5"/>
                        <rect x="11" y="3" width="6" height="6" rx="1" stroke="currentColor" strokeWidth="1.5"/>
                        <rect x="3" y="11" width="6" height="6" rx="1" stroke="currentColor" strokeWidth="1.5"/>
                        <rect x="11" y="11" width="6" height="6" rx="1" stroke="currentColor" strokeWidth="1.5"/>
                      </svg>
                      <span>General</span>
                    </h5>
                    <div className="hotel-detail-room-details-modal-amenities-list">
                      {roomDetailsModalData.generalAmenities.map((amenity, idx) => (
                        <div key={idx} className="hotel-detail-room-details-modal-amenity-item">{amenity}</div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Internet */}
                {roomDetailsModalData.internetAmenities.length > 0 && (
                  <div className="hotel-detail-room-details-modal-section">
                    <h5 className="hotel-detail-room-details-modal-section-title-with-icon">
                      <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M3 8C5.5 5.5 8 4 10 4C12 4 14.5 5.5 17 8" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
                        <path d="M5.5 11C7 9.5 8.5 8.5 10 8.5C11.5 8.5 13 9.5 14.5 11" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
                        <circle cx="10" cy="14" r="1.5" fill="currentColor"/>
                      </svg>
                      <span>Internet</span>
                    </h5>
                    <div className="hotel-detail-room-details-modal-amenities-list">
                      {roomDetailsModalData.internetAmenities.map((amenity, idx) => (
                        <div key={idx} className="hotel-detail-room-details-modal-amenity-item">{amenity}</div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Services */}
                {roomDetailsModalData.servicesAmenities.length > 0 && (
                  <div className="hotel-detail-room-details-modal-section">
                    <h5 className="hotel-detail-room-details-modal-section-title-with-icon">
                      <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M4 16L8 10L11 13L16 6" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                        <circle cx="16" cy="6" r="2" stroke="currentColor" strokeWidth="1.5"/>
                      </svg>
                      <span>Services</span>
                    </h5>
                    <div className="hotel-detail-room-details-modal-amenities-list">
                      {roomDetailsModalData.servicesAmenities.map((amenity, idx) => (
                        <div key={idx} className="hotel-detail-room-details-modal-amenity-item">{amenity}</div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Bed Types */}
                {roomDetailsModalData.roomDetailsData?.bedTypes && roomDetailsModalData.roomDetailsData.bedTypes.length > 0 && (
                  <div className="hotel-detail-room-details-modal-section">
                    <h5 className="hotel-detail-room-details-modal-section-title">Bed configuration</h5>
                    <div className="hotel-detail-room-details-modal-amenities-list">
                      {roomDetailsModalData.roomDetailsData.bedTypes.map((bedType, idx) => {
                        // Handle both string and object bed types
                        const bedTypeLabel = typeof bedType === 'string' 
                          ? bedType 
                          : `${bedType.quantity || 1} ${bedType.bedType || 'Bed'}${bedType.bedSize ? ` (${bedType.bedSize})` : ''}`;
                        return (
                          <div key={idx} className="hotel-detail-room-details-modal-amenity-item">{bedTypeLabel}</div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
      )}

      {/* Terms & Conditions Modal */}
      {showTermsModal && (
        <div className="hotel-detail-footer-modal-overlay" onClick={() => setShowTermsModal(false)}>
          <div className="hotel-detail-footer-modal hotel-detail-footer-modal-large" onClick={(e) => e.stopPropagation()}>
            <div className="hotel-detail-footer-modal-header">
              <h3 className="hotel-detail-footer-modal-title">Terms and conditions</h3>
              <button
                className="hotel-detail-footer-modal-close"
                onClick={() => setShowTermsModal(false)}
                aria-label="Close"
              >
                ×
              </button>
            </div>
            <div className="hotel-detail-footer-modal-content">
              <NuiteeTermsContent />
            </div>
          </div>
        </div>
      )}

      {/* Privacy Policy Modal */}
      {showPrivacyModal && (
        <div className="hotel-detail-footer-modal-overlay" onClick={() => setShowPrivacyModal(false)}>
          <div className="hotel-detail-footer-modal hotel-detail-footer-modal-large" onClick={(e) => e.stopPropagation()}>
            <div className="hotel-detail-footer-modal-header">
              <h3 className="hotel-detail-footer-modal-title">Privacy policy</h3>
              <button
                className="hotel-detail-footer-modal-close"
                onClick={() => setShowPrivacyModal(false)}
                aria-label="Close"
              >
                ×
              </button>
            </div>
            <div className="hotel-detail-footer-modal-content">
              <NuiteePrivacyContent />
            </div>
          </div>
        </div>
      )}

      {/* Contact Us Modal */}
      {showContactModal && (
        <div className="hotel-detail-footer-modal-overlay" onClick={() => setShowContactModal(false)}>
          <div className="hotel-detail-footer-modal hotel-detail-contact-modal" onClick={(e) => e.stopPropagation()}>
            <button
              className="hotel-detail-contact-modal-close"
              onClick={() => setShowContactModal(false)}
              aria-label="Close"
            >
              ×
            </button>
            <div className="hotel-detail-contact-modal-content">
              {/* Bell Icon */}
              <div className="hotel-detail-contact-icon">
                <svg width="80" height="80" viewBox="0 0 80 80" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <ellipse cx="40" cy="72" rx="12" ry="4" fill="#e0e0e0"/>
                  <path d="M40 8C38.8954 8 38 8.89543 38 10V14.1C28.6 15.5 22 23.5 22 33V48L16 58H64L58 48V33C58 23.5 51.4 15.5 42 14.1V10C42 8.89543 41.1046 8 40 8Z" fill="#FFD700" stroke="#E6C200" strokeWidth="2"/>
                  <path d="M32 58C32 62.4183 35.5817 66 40 66C44.4183 66 48 62.4183 48 58" stroke="#E6C200" strokeWidth="2"/>
                  <ellipse cx="40" cy="33" rx="8" ry="4" fill="#FFF5CC" opacity="0.6"/>
                </svg>
              </div>

              <h2 className="hotel-detail-contact-title">We&apos;re at your disposal</h2>
              <p className="hotel-detail-contact-subtitle">Our customer service is here to answer all your requests by mail or phone</p>

              <div className="hotel-detail-contact-methods">
                <a href="mailto:bookings@globethrivers.com" className="hotel-detail-contact-method">
                  <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <rect x="2" y="4" width="16" height="12" rx="2" stroke="#4A90D9" strokeWidth="1.5"/>
                    <path d="M2 6L10 11L18 6" stroke="#4A90D9" strokeWidth="1.5" strokeLinecap="round"/>
                  </svg>
                  <span>bookings@globethrivers.com</span>
                </a>
                <a href="tel:+18663383099" className="hotel-detail-contact-method">
                  <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M4.5 2.5C4.5 2.5 5.5 2 7 2C8.5 2 9 3 9 4V6C9 7 8 8 7 8C6 8 5.5 8.5 5 9.5C4.5 10.5 4 12 5 14C6 16 7.5 17 9 17.5C10.5 18 12 17.5 13 16.5C14 15.5 14 14 14 13C14 12 15 11 16 11H18C19 11 20 12 20 13.5C20 15 19.5 16.5 18 18C16.5 19.5 14 20 11 19.5C8 19 5 17 3 14C1 11 0.5 8 1 5.5C1.5 3 3 1.5 4.5 2.5Z" stroke="#4A90D9" strokeWidth="1.5" strokeLinecap="round"/>
                  </svg>
                  <span>+1 866 338 3099</span>
                </a>
                <a href="tel:+443308184701" className="hotel-detail-contact-method">
                  <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M4.5 2.5C4.5 2.5 5.5 2 7 2C8.5 2 9 3 9 4V6C9 7 8 8 7 8C6 8 5.5 8.5 5 9.5C4.5 10.5 4 12 5 14C6 16 7.5 17 9 17.5C10.5 18 12 17.5 13 16.5C14 15.5 14 14 14 13C14 12 15 11 16 11H18C19 11 20 12 20 13.5C20 15 19.5 16.5 18 18C16.5 19.5 14 20 11 19.5C8 19 5 17 3 14C1 11 0.5 8 1 5.5C1.5 3 3 1.5 4.5 2.5Z" stroke="#4A90D9" strokeWidth="1.5" strokeLinecap="round"/>
                  </svg>
                  <span>+44 330 818 4701</span>
                </a>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default HotelDetailView;
